/**
 * Projects Service
 * Handles all project-related API operations including stages, blocks, units, typologies, etc.
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS, API_BASE_URL } from '@/config/api.config';
import { PaginatedResponse } from './leadsService';

// Types
export interface Project {
  id: string;
  code: string;
  name: string;
  slug: string;
  description?: string;
  company: string; // UUID de la compañía
  company_name?: string; // Nombre de la compañía
  status: 'planning' | 'pre_launch' | 'active' | 'sold_out' | 'completed' | 'on_hold' | 'cancelled';
  type: 'residential' | 'commercial' | 'mixed_use' | 'office' | 'retail' | 'mixed' | 'industrial';
  featured: boolean;
  
  // Location
  address?: string;
  city: string;
  state: string;
  country: string;
  zipcode?: string;
  postalCode?: string; // Alias for zipcode
  latitude?: number;
  longitude?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  
  // Pricing
  price_from?: number;
  price_to?: number;
  currency: string;
  
  // Units
  total_units: number;
  available_units: number;
  sold_units: number;
  reserved_units: number;
  occupancy_rate: number;

  // Real unit counts calculated from actual units
  total_units_count?: number;
  sold_units_count?: number;
  reserved_units_count?: number;
  available_units_real?: number;
  
  // Media
  main_image?: string;
  logo?: string;
  video_url?: string;
  virtual_tour_url?: string;

  // Branding
  primary_color?: string;
  secondary_color?: string;
  background_color?: string;
  accent_color?: string;

  // Landing page / marketing fields
  carousel_images?: Array<{ url: string; title?: string; order?: number }>;
  logo_dark?: string;
  neighborhood?: string;
  zone?: string;
  tagline?: string;
  brochure_url?: string;
  meta_title?: string;
  meta_description?: string;

  // Metadata
  lead_count: number;
  quote_count: number;
  view_count: number;
  
  // Dates
  start_date?: string;
  expected_completion_date?: string;
  completion_date?: string;
  
  created_at: string;
  createdAt?: string; // Alias for created_at
  updated_at: string;
  
  // Additional fields for UI
  website?: string;
  companyId?: string; // Alias for company
  measureUnit?: 'm²' | 'ft²';
  measure_unit?: 'm²' | 'ft²'; // Backend field
  showPrices?: boolean;
  show_prices?: boolean; // Backend field
  allowQuote?: boolean;
  allow_quote?: boolean; // Backend field
  crmList?: Array<{
    integration: string;
    crm: string;
    config: string;
  }>;
  orbitViewProject?: string;
  orbitViewAmenities?: string;
  // Backend sends snake_case versions
  orbit_view_project?: string;
  orbit_view_amenities?: string;
  
  // Financial Configuration
  interest_rate?: number;
  financial_discount_rate?: number;
  pe_commercial_threshold?: number;
  pe_financial_threshold?: number;
  default_terms?: string;
  data_policy_url?: string;

  // Branding
  background_color?: string;
  accent_color?: string;
  status_colors?: Record<string, string>; // Unit status colors (hex colors)
  public_status_mapping?: Record<string, string>; // Maps internal statuses to public-facing statuses
  orbit_view_theme?: any; // OrbitView theme configuration (colors, promo, etc.)
  orbit_view_slug?: string; // OrbitView slug for public URL

  // Inherited fields from company (which inherits from market)
  effective_currency?: string;
  effective_currency_symbol?: string;
  effective_country?: string;

  // Identification types configuration
  allowed_identification_types?: Array<{
    id: string;
    code: string;
    name: string;
    country_code?: string;
    is_active: boolean;
    is_default: boolean;
    display_order: number;
  }>;

  // Media items (unified media storage)
  media_items?: any[];

  // Metadata
  metadata?: any;

  // Markets where this project is available (M2M)
  available_in_markets?: Array<{
    id: string;
    country_code: string;
    country_name: string;
    flag_emoji: string;
    currency_code: string | null;
  }>;
  available_in_markets_ids?: string[]; // Write-only field for updates

  // Quote form configuration (income/savings/severance ranges)
  quote_form_config?: {
    income?: { type: 'free_text' | 'ranges'; ranges?: Array<{ label: string; min: number; max: number | null }> };
    savings?: { type: 'free_text' | 'ranges'; ranges?: Array<{ label: string; min: number; max: number | null }> };
    severance?: { type: 'free_text' | 'ranges'; ranges?: Array<{ label: string; min: number; max: number | null }> };
  };
}

export interface Stage {
  id: string;
  project: string;
  code: string;
  name: string;
  slug: string;
  stage_number: string | number;
  description?: string;
  status: 'planning' | 'active' | 'completed' | 'on-hold';
  glb_code?: string;
  
  // Housing typology
  housing_typology?: 'VIS' | 'VIP' | 'NOVIS' | string;
  project_type?: string | null; // ID de la tipología de vivienda
  project_type_detail?: {
    id: string;
    code: string;
    name: string;
    color: string;
    estimated_interest_rate: number | null;
    estimated_loan_term_years: number | null;
  };
  
  // Financial Terms
  separation?: number;
  down_payment?: number;
  discount?: number;
  projected_increase?: number;
  
  // Pricing
  price_from?: number;
  price_to?: number;
  
  // Dates
  start_date?: string;
  closing_date?: string;
  
  // Media
  brochure_url?: string;
  main_image?: string;
  media_items?: any[];
  
  created_at: string;
  updated_at: string;
}

export interface Block {
  id: string;
  stage: string;
  code: string;
  name: string;
  slug: string;
  description?: string;
  glb_code?: string;
  status: 'planning' | 'construction' | 'completed' | 'on-hold';

  // Configuration
  floors: number;
  units_per_floor?: number;
  total_units?: number;

  // Visualization
  target_frame?: string;

  // Pricing
  pricing_scheme_id?: string | null;
  pricing_scheme_detail?: PricingScheme | null;

  // Fractional Ownership
  allows_fractional_ownership?: boolean;
  number_of_fractions?: number;

  // Media
  media_items?: any[];

  created_at: string;
  updated_at: string;
}

export interface Typology {
  id: string;
  stage: string;
  code: string;
  name: string;
  slug: string;
  description?: string;
  glb_code?: string;
  
  // Specifications (Distribution)
  bedrooms?: string | number;
  bathrooms?: string | number;
  
  // Areas (m²) - Reference values for this typology
  area_sqm?: number;
  private_area?: number;
  balcony_area_sqm?: number;
  terrace_area?: number;
  mezzanine_area?: number;
  garden_area?: number;
  
  // Pricing - Reference prices for this typology
  price_from?: number;
  price_to?: number;
  base_price?: number;
  
  // Visualization
  target_frame?: string;
  
  // Media
  main_image?: string;
  floor_plan_url?: string;
  media_items?: any[];

  // Computed counts
  units_count?: number;
  available_units_count?: number;
  quotes_count?: number;

  // Area ranges from units
  area_ranges?: {
    private_area?: { min: number; max: number; label: string };
    build_area?: { min: number; max: number; label: string };
    balcony_area?: { min: number; max: number; label: string };
    terrace_area?: { min: number; max: number; label: string };
  };

  // Price range from available units
  price_range?: {
    min: number | null;
    max: number | null;
    currency: string;
  };

  // Context info (read-only)
  project_name?: string;
  stage_name?: string;

  created_at: string;
  updated_at: string;
}

export interface TypologiesStatsResponse {
  total: number;
  total_units: number;
  total_quotes: number;
  by_bedrooms: Record<string, { name: string; count: number }>;
  by_project: Record<string, { name: string; count: number }>;
  by_stage: Record<string, { name: string; count: number }>;
}

export interface BulkTypologyActionResponse {
  success: boolean;
  updated_count?: number;
  deleted_count?: number;
  message: string;
}

export interface AmenityCategory {
  id: string;
  code: string;
  name: string;
  description?: string;
  icon: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AmenityStatus {
  id: string;
  code: string;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AmenitiesStatsResponse {
  total: number;
  by_category: Record<string, { name: string; color: string; count: number }>;
  by_status: Record<string, { name: string; color: string; count: number }>;
  by_parent_type: {
    project: number;
    stage: number;
    block: number;
  };
}

export interface BulkAmenityActionResponse {
  success: boolean;
  updated_count: number;
  message: string;
}

export interface Amenity {
  id: string;
  parent_type: 'project' | 'stage' | 'block';
  parent_id: string;
  name: string;
  slug: string;
  glb_code?: string;
  icon?: string;
  description?: string;
  category?: string;
  category_id?: string;
  category_detail?: AmenityCategory;
  area?: string;
  floor?: number;
  status?: string;
  status_id?: string;
  status_detail?: AmenityStatus;
  capacity?: number;
  schedule?: string;
  requires_reservation?: boolean;
  has_additional_cost?: boolean;
  additional_cost_amount?: number;
  is_exclusive?: boolean;
  media_items?: any[];
  metadata?: any;
  created_at: string;
  updated_at: string;
}

// Note: OrbitView interface is defined in @/types/orbitView.ts

export interface Unit {
  id: string;
  company: string;
  block: string;
  typology?: string | null;

  // Basic Information
  name: string;
  code: string;
  unit_number: string;
  slug: string;
  glb_code?: string;
  unit_type?: string;
  typology_slug?: string;
  status: 'available' | 'reserved' | 'sold' | 'blocked' | 'optioned' | 'unavailable';

  // Specifications
  floor?: string;
  bedrooms?: string;
  bathrooms?: string;

  // Areas
  private_area?: number;
  build_area?: number;
  area_sqm?: number;
  terrace_area?: number;
  terrace_sqm?: number;
  terrace_private_area?: number;
  balcony_area?: number;
  mezzanine_area?: number;
  garden_area?: number;
  rooftop_area?: number;

  // Counts
  terrace_number?: number;
  balcony_number?: number;

  // Pricing - Legacy
  price?: number;
  fixed_price?: number;
  price_type?: 'fixedPrice' | 'smlvPrice';
  currency?: string;
  price_per_sqm?: number;

  // Advanced Pricing System
  pricing_scheme_override?: string | null;
  pricing_scheme_override_id?: string | null;
  pricing_scheme_detail?: {
    id: string;
    name: string;
    code: string;
    base_price?: number | null;
    source: 'unit_override' | 'block';
  } | null;
  use_calculated_price?: boolean;
  override_floor_increment?: boolean;
  override_view_adjustment?: boolean;

  // NEW: Full configuration for overrides (same as PricingScheme)
  custom_floor_increment_config?: {
    enabled?: boolean;
    type?: 'percentage' | 'absolute';
    value?: number;
    ranges?: Array<{
      from: number;
      to: number;
      value: number;
    }>;
  };
  custom_view_adjustment_config?: {
    type?: 'percentage' | 'fixed_value' | 'absolute';
    value?: number;
    view_name?: string;
  };

  // DEPRECATED: Legacy fields for backward compatibility
  custom_floor_increment_value?: number | null;
  custom_view_adjustment_value?: number | null;
  price_breakdown?: {
    areas?: Record<string, {
      area: number;
      price_per_sqm: number;
      total: number;
    }>;
    base_price?: number;
    floor_increment?: number;
    view_adjustment?: number;
    scheme_name?: string;
    scheme_id?: string;
    calculation_date?: string;
    [key: string]: any;
  };
  calculated_price?: number | null;
  calculated_price_info?: {
    price: number;
    breakdown: any;
    last_calculated?: string | null;
  } | null;
  price_last_calculated?: string | null;

  // Area Price Overrides - Allow unit-level override of pricing scheme's area prices
  override_area_prices?: boolean;
  custom_price_per_sqm_build?: number | null;
  custom_price_per_sqm_balcony?: number | null;
  custom_price_per_sqm_terrace?: number | null;
  custom_price_per_sqm_mezzanine?: number | null;
  custom_price_per_sqm_garden?: number | null;
  custom_price_per_sqm_rooftop?: number | null;

  // Final price and override indicators (computed from backend)
  final_price?: number | null;
  is_price_overridden?: boolean;
  has_any_override?: boolean;

  // Configurable area system
  area_values_detail?: UnitAreaValue[];
  custom_area_prices_detail?: UnitCustomAreaPrice[];

  // Complementary units
  complementary_unit?: Record<string, any>;
  complementary_units?: any[];

  // Features
  features?: any[];
  orientation?: string;
  view_type?: string;

  // Media
  floor_plan_image?: string;
  images?: any[];
  virtual_tour_url?: string;

  // SVG Visualization
  svg_element_id?: string;
  svg_clickable_area?: string;

  // Documents
  documents?: any[];

  // Reservation/Sale info
  reserved_by?: string | null;
  reserved_date?: string | null;
  sold_to?: string;
  sold_date?: string | null;

  // Fractional Ownership Override
  override_fractional_ownership?: boolean | null;
  override_number_of_fractions?: number | null;
  allows_fractional_ownership_computed?: boolean;
  number_of_fractions_computed?: number;

  // Hierarchy (computed fields)
  project_name?: string;
  project_id?: string;
  stage_name?: string;
  stage_id?: string;
  block_name?: string;
  typology_data?: any;

  // Metadata
  metadata?: Record<string, any>;

  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
}

export interface ViewPriceAdjustment {
  id: string;
  company: string;
  applicability: 'project' | 'stage' | 'block' | 'pricing_scheme';
  reference_id: string;
  name: string;
  code: string;
  slug: string;
  description?: string;
  view_type: string;
  adjustment_type: 'percentage' | 'fixed_value';
  adjustment_value: number;
  percentage_base?: 'base_price' | 'base_plus_floor' | 'per_sqm';
  adjustment_display: string;
  priority: number;
  is_active: boolean;
  valid_from?: string;
  valid_until?: string;
  color?: string;
  icon?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface ViewPriceAdjustmentListItem {
  id: string;
  name: string;
  code: string;
  view_type: string;
  adjustment_type: 'percentage' | 'fixed_value';
  adjustment_value: number;
  percentage_base?: 'base_price' | 'base_plus_floor' | 'per_sqm';
  adjustment_display: string;
  priority: number;
  is_active: boolean;
  color?: string;
  icon?: string;
}

export interface PricingScheme {
  id: string;
  company: string;
  scheme_type: 'project' | 'stage' | 'unit';
  reference_id: string;
  name: string;
  code: string;
  slug: string;
  description?: string;

  // Pricing Model
  pricing_model: 'fixed' | 'dynamic' | 'tiered' | 'custom';
  base_price?: number;
  currency: string;

  // Base Pricing Scheme (for incremental/derived lists)
  base_pricing_scheme?: string | null;
  base_pricing_scheme_detail?: {
    id: string;
    name: string;
    code: string;
    base_price?: string | null;
  } | null;
  base_adjustment_type: 'percentage' | 'absolute';
  base_adjustment_value: number;

  // Area-Specific Pricing (Price per square meter by area type)
  price_per_sqm_build: number;
  price_per_sqm_balcony: number;
  price_per_sqm_terrace: number;
  price_per_sqm_mezzanine: number;
  price_per_sqm_garden: number;
  price_per_sqm_rooftop: number;

  // Floor Increments Configuration
  floor_increments: {
    enabled?: boolean;
    type?: 'percentage' | 'absolute';
    value?: number; // Simple increment value (used when not using ranges)
    ranges?: Array<{
      from: number;
      to: number;
      value: number;
    }>;
  };

  // Financial Terms
  separation_percentage: number;
  separation_mode?: 'percentage' | 'fixed';
  separation_amount?: number | null;
  down_payment_percentage: number;
  down_payment_min?: number;

  // Financing
  max_financing_percentage: number;
  default_interest_rate?: number;
  max_financing_years: number;

  // Sequential Ordering (for progressive pricing)
  list_order: number;

  // Validity
  valid_from?: string;
  valid_until?: string;
  is_active: boolean;

  // Configuration
  allow_custom_plans: boolean;
  requires_approval: boolean;
  allow_unit_override: boolean;

  // View adjustments (Legacy - deprecated)
  view_delta?: number;
  view_deltas?: Record<string, any>;
  view_adjustments?: ViewPriceAdjustmentListItem[];

  // Payment Plans
  payment_plans?: any[];
  payment_plans_count?: number;
  is_currently_valid?: boolean;
  has_base_scheme?: boolean;

  // Context Details (new fields)
  stage_detail?: {
    id: string;
    name: string;
    slug: string;
    code: string;
    stage_number: string;
    project_id: string;
    project_name: string;
  } | null;
  project_detail?: {
    id: string;
    name: string;
    slug: string;
    code: string;
  } | null;

  created_at: string;
  updated_at: string;
}

/**
 * Pricing Scheme Progression - Automatic price list generation
 */
export interface PricingSchemeProgression {
  id: string;
  company: string;
  name: string;
  code: string;
  slug: string;

  // Relations
  stage: string;
  stage_detail?: {
    id: string;
    name: string;
    code: string;
  } | null;

  base_pricing_scheme: string;
  base_pricing_scheme_detail?: {
    id: string;
    name: string;
    code: string;
    list_order: number;
  } | null;

  last_created_scheme?: string | null;
  last_created_scheme_detail?: {
    id: string;
    name: string;
    code: string;
    list_order: number;
  } | null;

  // NEW: Current active scheme tracking
  current_active_scheme?: string | null;
  current_active_scheme_detail?: {
    id: string;
    name: string;
    code: string;
    list_order: number;
  } | null;

  // Trigger Configuration
  trigger_type: 'units_sold' | 'percentage_sold' | 'sales_value' | 'days_elapsed';
  trigger_type_display?: string;
  trigger_value: number;

  // Increment Configuration
  increment_type: 'percentage' | 'absolute' | 'manual';
  increment_type_display?: string;
  percentage_increment?: number;
  absolute_increments?: Record<string, number>; // JSONField with area-specific increments
  manual_config_template?: Record<string, any>; // JSONField for custom configuration

  // List Naming
  new_list_name_template: string;

  // Progression Control
  is_active: boolean;
  sequence_number: number;
  last_triggered_at?: string | null;
  progression_started_at?: string | null;

  // Monitoring Data
  current_units_sold: number;
  current_sales_value: number;
  units_sold_since_last_generation: number; // NEW: Smart counter
  is_executing: boolean; // NEW: Concurrency lock
  execution_history: Array<{
    timestamp: string;
    scheme_id: string;
    scheme_name: string;
    trigger_value: number;
    units_sold: number;
    sequence: number;
  }>; // NEW: Execution history

  // Metadata
  notes?: string;
  metadata?: Record<string, any>;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Pricing Scheme Progression List Item - Lightweight version for list views
 */
export interface PricingSchemeProgressionListItem {
  id: string;
  name: string;
  code: string;
  stage_name?: string;
  base_scheme_name?: string;
  trigger_type: string;
  trigger_type_display?: string;
  trigger_value: number;
  increment_type: string;
  increment_type_display?: string;
  percentage_increment?: number;
  is_active: boolean;
  sequence_number: number;
  last_triggered_at?: string | null;
  current_units_sold: number;
  current_sales_value: number;
  // NEW FIELDS
  units_sold_since_last_generation: number;
  is_executing: boolean;
  current_active_scheme?: string | null;
  current_active_scheme_name?: string | null;
}

/**
 * Request payload for manual price list generation
 */
export interface GenerateNextPriceListRequest {
  effective_date?: string; // ISO date string, optional (defaults to today)
}

/**
 * Response from manual price list generation
 */
export interface GenerateNextPriceListResponse {
  success: boolean;
  pricing_scheme?: PricingScheme;
  message: string;
  error?: string;
}

/**
 * Response from checking trigger status
 */
export interface CheckTriggerResponse {
  triggered: boolean;
  current_value: number;
  trigger_value: number;
  percentage: number;
  message: string;
}

export interface ProjectStats {
  total_units: number;
  available_units: number;
  sold_units: number;
  reserved_units: number;
  occupancy_rate: number;
  lead_count: number;
  quote_count: number;
  view_count: number;
  avg_unit_price: number;
  avg_unit_area: number;
  units_by_type: Record<string, number>;
  units_by_floor: Record<string, number>;
}

/**
 * Area Type - Configurable area types for pricing
 */
export interface UnitAreaValue {
  id: string;
  unit: string;
  area_type: string;
  area_type_detail?: AreaType;
  value: number;
  notes?: string;
  company: string;
  created_at: string;
  updated_at: string;
}

export interface UnitCustomAreaPrice {
  id: string;
  unit: string;
  area_type: string;
  area_type_detail?: AreaType;
  pricing_type: 'simple' | 'tiered';
  price: number;
  tiered_prices?: Array<{
    from: number;
    to?: number;
    price: number;
  }>;
  reason?: string;
  notes?: string;
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Area Type Subcategory - Subcategory for area types (e.g., Parking: Covered, Uncovered, Double)
 */
export interface AreaTypeSubcategory {
  id: string;
  area_type: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order: number;
  is_active: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * Area Type Subcategory List - Lightweight version for listings
 */
export interface AreaTypeSubcategoryList {
  id: string;
  code: string;
  name: string;
  icon?: string;
  color?: string;
  sort_order: number;
  is_active: boolean;
}

export interface AreaType {
  id: string;
  code: string;
  name: string;
  description?: string;
  measurement_type: 'sqm' | 'unit' | 'linear';
  category: 'area' | 'parking' | 'storage' | 'amenity' | 'special' | 'other';
  is_required: boolean;
  is_active: boolean;
  is_calculated: boolean;
  is_informational_only: boolean;
  calculation_formula?: {
    type: 'sum' | 'product' | 'custom';
    sources?: string[];
    formula?: string;
  };
  validation_rules: {
    min: number;
    max: number;
    step: number;
    allow_zero: boolean;
  };
  supports_tiered_pricing: boolean;
  is_assignable_complement: boolean;
  has_subcategories: boolean;
  subcategories?: AreaTypeSubcategoryList[];
  icon?: string;
  color?: string;
  sort_order: number;
  metadata?: Record<string, any>;
  projects_count?: number;  // Number of projects using this area type
  created_at: string;
  updated_at: string;
}

/**
 * Project Area Type Subcategory - Project-specific subcategories for area types
 */
export interface ProjectAreaTypeSubcategory {
  id: string;
  project_area_type: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order: number;
  is_active: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * Project Area Type Subcategory List - Lightweight version for listings
 */
export interface ProjectAreaTypeSubcategoryList {
  id: string;
  code: string;
  name: string;
  icon?: string;
  color?: string;
  sort_order: number;
  is_active: boolean;
  metadata?: Record<string, any>;
}

/**
 * Project Area Type - Area types assigned to a project (first level of hierarchy)
 */
export interface ProjectAreaType {
  id: string;
  project: string;
  area_type: string;
  area_type_id?: string;
  area_type_detail?: AreaType;
  default_price?: number | null;
  is_required: boolean;
  is_active: boolean;
  is_assignable_complement?: boolean;
  sort_order: number;
  notes?: string;
  subcategories?: ProjectAreaTypeSubcategoryList[];
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Stage Area Type - Area types assigned to a stage (second level - from project)
 */
export interface StageAreaType {
  id: string;
  stage: string;
  project_area_type: string;
  project_area_type_id?: string;
  project_area_type_detail?: {
    id: string;
    project_id: string;
    area_type_id: string;
    default_price: string | null;
    is_required: boolean;
  };
  area_type_detail?: {
    id: string;
    code: string;
    name: string;
    measurement_type: string;
    category: string;
    icon?: string;
    color?: string;
    has_subcategories?: boolean;
  };
  default_price?: number | null;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
  notes?: string;
  enabled_subcategories_detail?: Array<{
    id: string;
    code: string;
    name: string;
    icon?: string;
  }>;
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Block Area Type - Area types assigned to a block (third level - from stage)
 */
export interface BlockAreaType {
  id: string;
  block: string;
  stage_area_type: string;
  stage_area_type_id?: string;
  stage_area_type_detail?: {
    id: string;
    stage_id: string;
    project_area_type_id: string;
    default_price: string | null;
    is_required: boolean;
  };
  area_type_detail?: {
    id: string;
    code: string;
    name: string;
    measurement_type: string;
    category: string;
    icon?: string;
    color?: string;
  };
  default_price?: number | null;
  is_active: boolean;
  sort_order: number;
  notes?: string;
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Lightweight list versions for dropdowns
 */
export interface ProjectAreaTypeList {
  id: string;
  area_type_id: string;
  area_type_code: string;
  area_type_name: string;
  area_type_measurement_type: string;
  area_type_icon?: string;
  area_type_category?: string;
  default_price?: number | null;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface StageAreaTypeList {
  id: string;
  project_area_type_id: string;
  area_type_id: string;
  area_type_code: string;
  area_type_name: string;
  area_type_measurement_type: string;
  area_type_icon?: string;
  area_type_category?: string;
  default_price?: number | null;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
  has_subcategories?: boolean;
  subcategories?: Array<{
    id: string;
    code: string;
    name: string;
    icon?: string;
  }>;
  enabled_subcategories?: Array<{
    id: string;
    code: string;
    name: string;
    icon?: string;
  }>;
  // Area type detail from backend serializer
  area_type_detail?: {
    id: string;
    code: string;
    name: string;
    measurement_type: string;
    category: string;
    icon?: string;
    color?: string;
    has_subcategories: boolean;
    is_informational_only: boolean;
    is_assignable_complement: boolean;
  };
}

export interface BlockAreaTypeList {
  id: string;
  stage_area_type_id: string;
  area_type_id: string;
  area_type_code: string;
  area_type_name: string;
  area_type_measurement_type: string;
  area_type_icon?: string;
  area_type_category?: string;
  default_price?: number | null;
  is_active: boolean;
  sort_order: number;
}

/**
 * Complementary Unit - Individual assignable unit at block level (parking, storage, etc.)
 */
export interface ComplementaryUnit {
  id: string;
  block: string;
  block_name?: string;
  area_type: string;
  area_type_detail?: AreaType;
  // Legacy global subcategory (for backwards compatibility)
  subcategory?: string;
  subcategory_detail?: AreaTypeSubcategoryList;
  // Project-specific subcategory (new)
  project_subcategory?: string;
  project_subcategory_detail?: ProjectAreaTypeSubcategoryList;
  identifier: string;
  area?: number;
  location?: string;
  estimated_price: number;
  status: 'available' | 'assigned' | 'reserved' | 'sold';
  notes?: string;
  metadata?: Record<string, any>;
  current_assignment?: {
    id: string;
    unit_id: string;
    unit_name: string;
    sale_price?: number;
    assigned_at: string;
  };
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Complementary Unit List - Lightweight version for listings
 */
export interface ComplementaryUnitList {
  id: string;
  identifier: string;
  area?: number;
  location?: string;
  estimated_price: number;
  status: 'available' | 'assigned' | 'reserved' | 'sold';
  area_type: string;
  area_type_name: string;
  area_type_icon?: string;
  area_type_code: string;
  block: string;
  block_name: string;
  stage_name: string;
  // Legacy global subcategory
  subcategory?: string;
  subcategory_name?: string;
  subcategory_code?: string;
  // Project-specific subcategory (new)
  project_subcategory?: string;
  project_subcategory_name?: string;
  project_subcategory_code?: string;
  assigned_to_unit?: {
    unit_id: string;
    unit_name: string;
  };
  updated_at?: string;
}

/**
 * Unit Complementary Assignment - Assignment of a complementary unit to a main unit
 */
export interface UnitComplementaryAssignment {
  id: string;
  unit: string;
  unit_name?: string;
  complementary_unit: string;
  complementary_unit_detail?: ComplementaryUnitList;
  sale_price?: number;
  effective_price?: number;
  assigned_at: string;
  notes?: string;
  company: string;
  created_at: string;
  updated_at: string;
}

/**
 * Complementary Unit Stats - Statistics for a block
 */
export interface ComplementaryUnitStats {
  total: number;
  available: number;
  assigned: number;
  reserved: number;
  sold: number;
  total_estimated_value: number;
  by_type: Array<{
    area_type_id: string;
    area_type_name: string;
    area_type_icon?: string;
    count: number;
    available_count: number;
    total_value: number;
  }>;
}

/**
 * Pricing Scheme Area Price - Price configuration for an area type in a pricing scheme
 */
export interface PricingSchemeAreaPrice {
  id: string;
  company: string;
  pricing_scheme: string;
  area_type: string;
  area_type_id?: string;  // write-only
  area_type_detail?: AreaType;
  subcategory?: string;
  subcategory_id?: string;  // write-only
  subcategory_detail?: AreaTypeSubcategoryList;
  project_subcategory?: string;
  project_subcategory_id?: string;  // write-only
  project_subcategory_detail?: AreaTypeSubcategoryList;
  pricing_type: 'simple' | 'tiered';
  price: number;
  tiered_prices?: Array<{
    from: number;
    to?: number;
    price: number;
  }>;
  is_active: boolean;
  is_optional: boolean;
  display_order: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Unit Area Value - Area value for a specific unit
 */
export interface UnitAreaValue {
  id: string;
  company: string;
  unit: string;
  area_type: string;
  area_type_detail?: AreaType;
  value: number;
  created_at: string;
  updated_at: string;
}

/**
 * Projects Service
 */
export const projectsService = {
  // ============================================
  // PROJECTS
  // ============================================
  
  /**
   * Get all projects
   */
  async getProjects(params?: Record<string, any>): Promise<PaginatedResponse<Project>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.BASE;
    
    return apiClient.get<PaginatedResponse<Project>>(endpoint);
  },

  /**
   * Get project by ID
   */
  async getProjectById(id: string, params?: Record<string, string>): Promise<Project> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiClient.get<Project>(API_ENDPOINTS.PROJECTS.DETAIL(id) + queryString);
  },

  /**
   * Create new project
   */
  async createProject(data: Partial<Project>): Promise<Project> {
    return apiClient.post<Project>(API_ENDPOINTS.PROJECTS.BASE, data);
  },

  /**
   * Update project
   */
  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    return apiClient.patch<Project>(API_ENDPOINTS.PROJECTS.DETAIL(id), data);
  },

  /**
   * Delete project
   */
  async deleteProject(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.DETAIL(id));
  },

  /**
   * Get project statistics
   */
  async getProjectStats(id: string): Promise<ProjectStats> {
    return apiClient.get<ProjectStats>(API_ENDPOINTS.PROJECTS.STATS(id));
  },

  /**
   * Get project units
   */
  async getProjectUnits(id: string): Promise<Unit[]> {
    return apiClient.get<Unit[]>(API_ENDPOINTS.PROJECTS.UNITS(id));
  },

  /**
   * Get all amenities for a project
   */
  async getProjectAmenities(id: string): Promise<Amenity[]> {
    return apiClient.get<Amenity[]>(API_ENDPOINTS.PROJECTS.AMENITIES(id));
  },

  /**
   * Get available units for a project
   */
  async getAvailableUnits(id: string): Promise<Unit[]> {
    return apiClient.get<Unit[]>(API_ENDPOINTS.PROJECTS.AVAILABLE_UNITS(id));
  },

  /**
   * Get featured projects
   */
  async getFeaturedProjects(): Promise<Project[]> {
    const response = await apiClient.get<PaginatedResponse<Project>>(
      API_ENDPOINTS.PROJECTS.FEATURED
    );
    return response.results || [];
  },

  /**
   * Get projects by location
   */
  async getProjectsByLocation(): Promise<Record<string, any>> {
    return apiClient.get(API_ENDPOINTS.PROJECTS.BY_LOCATION);
  },

  // ============================================
  // STAGES
  // ============================================
  
  /**
   * Get all stages
   */
  async getStages(params?: Record<string, any>): Promise<PaginatedResponse<Stage>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.STAGES}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.STAGES;
    
    return apiClient.get<PaginatedResponse<Stage>>(endpoint);
  },

  /**
   * Get stage by ID
   */
  async getStageById(id: string): Promise<Stage> {
    return apiClient.get<Stage>(API_ENDPOINTS.PROJECTS.STAGE_DETAIL(id));
  },

  /**
   * Create new stage
   */
  async createStage(data: Partial<Stage>): Promise<Stage> {
    return apiClient.post<Stage>(API_ENDPOINTS.PROJECTS.STAGES, data);
  },

  /**
   * Update stage
   */
  async updateStage(id: string, data: Partial<Stage>): Promise<Stage> {
    return apiClient.patch<Stage>(API_ENDPOINTS.PROJECTS.STAGE_DETAIL(id), data);
  },

  /**
   * Delete stage
   */
  async deleteStage(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.STAGE_DETAIL(id));
  },

  /**
   * Get blocks for a stage
   */
  async getStageBlocks(id: string): Promise<Block[]> {
    return apiClient.get<Block[]>(API_ENDPOINTS.PROJECTS.STAGE_BLOCKS(id));
  },

  /**
   * Get typologies for a stage
   */
  async getStageTypologies(id: string): Promise<Typology[]> {
    return apiClient.get<Typology[]>(API_ENDPOINTS.PROJECTS.STAGE_TYPOLOGIES(id));
  },

  /**
   * Get amenities for a stage
   */
  async getStageAmenities(id: string): Promise<Amenity[]> {
    return apiClient.get<Amenity[]>(API_ENDPOINTS.PROJECTS.STAGE_AMENITIES(id));
  },

  /**
   * Get pricing schemes for a stage
   */
  async getStagePricingSchemes(id: string): Promise<PricingScheme[]> {
    return apiClient.get<PricingScheme[]>(API_ENDPOINTS.PROJECTS.STAGE_PRICING_SCHEMES(id));
  },

  // ============================================
  // BLOCKS
  // ============================================
  
  /**
   * Get all blocks
   */
  async getBlocks(params?: Record<string, any>): Promise<PaginatedResponse<Block>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.BLOCKS}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.BLOCKS;
    
    return apiClient.get<PaginatedResponse<Block>>(endpoint);
  },

  /**
   * Get block by ID
   */
  async getBlockById(id: string): Promise<Block> {
    return apiClient.get<Block>(API_ENDPOINTS.PROJECTS.BLOCK_DETAIL(id));
  },

  /**
   * Create new block
   */
  async createBlock(data: Partial<Block>): Promise<Block> {
    return apiClient.post<Block>(API_ENDPOINTS.PROJECTS.BLOCKS, data);
  },

  /**
   * Update block
   */
  async updateBlock(id: string, data: Partial<Block>): Promise<Block> {
    return apiClient.patch<Block>(API_ENDPOINTS.PROJECTS.BLOCK_DETAIL(id), data);
  },

  /**
   * Delete block
   */
  async deleteBlock(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.BLOCK_DETAIL(id));
  },

  /**
   * Get units for a block
   */
  async getBlockUnits(id: string): Promise<Unit[]> {
    return apiClient.get<Unit[]>(API_ENDPOINTS.PROJECTS.BLOCK_UNITS(id));
  },

  /**
   * Get amenities for a block
   */
  async getBlockAmenities(id: string): Promise<Amenity[]> {
    return apiClient.get<Amenity[]>(API_ENDPOINTS.PROJECTS.BLOCK_AMENITIES(id));
  },

  // ============================================
  // TYPOLOGIES
  // ============================================
  
  /**
   * Get all typologies
   */
  async getTypologies(params?: Record<string, any>): Promise<PaginatedResponse<Typology>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.TYPOLOGIES}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.TYPOLOGIES;
    
    return apiClient.get<PaginatedResponse<Typology>>(endpoint);
  },

  /**
   * Get typology by ID
   */
  async getTypologyById(id: string): Promise<Typology> {
    return apiClient.get<Typology>(API_ENDPOINTS.PROJECTS.TYPOLOGY_DETAIL(id));
  },

  /**
   * Create new typology
   */
  async createTypology(data: Partial<Typology>): Promise<Typology> {
    return apiClient.post<Typology>(API_ENDPOINTS.PROJECTS.TYPOLOGIES, data);
  },

  /**
   * Update typology
   */
  async updateTypology(id: string, data: Partial<Typology>): Promise<Typology> {
    return apiClient.patch<Typology>(API_ENDPOINTS.PROJECTS.TYPOLOGY_DETAIL(id), data);
  },

  /**
   * Delete typology
   */
  async deleteTypology(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.TYPOLOGY_DETAIL(id));
  },

  /**
   * Get units for a typology
   */
  async getTypologyUnits(id: string): Promise<Unit[]> {
    return apiClient.get<Unit[]>(API_ENDPOINTS.PROJECTS.TYPOLOGY_UNITS(id));
  },

  /**
   * Get typology stats
   */
  async getTypologiesStats(params?: Record<string, any>): Promise<TypologiesStatsResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `${API_ENDPOINTS.PROJECTS.TYPOLOGIES}stats/?${queryParams.toString()}`
      : `${API_ENDPOINTS.PROJECTS.TYPOLOGIES}stats/`;

    return apiClient.get<TypologiesStatsResponse>(endpoint);
  },

  /**
   * Bulk update typology stage
   */
  async bulkUpdateTypologyStage(data: { typology_ids: string[]; stage_id: string | null }): Promise<BulkTypologyActionResponse> {
    return apiClient.post<BulkTypologyActionResponse>(
      `${API_ENDPOINTS.PROJECTS.TYPOLOGIES}bulk-update-stage/`,
      data
    );
  },

  /**
   * Bulk delete typologies
   */
  async bulkDeleteTypologies(data: { typology_ids: string[] }): Promise<BulkTypologyActionResponse> {
    return apiClient.post<BulkTypologyActionResponse>(
      `${API_ENDPOINTS.PROJECTS.TYPOLOGIES}bulk-delete/`,
      data
    );
  },

  // ============================================
  // AMENITIES
  // ============================================
  
  /**
   * Get all amenities
   */
  async getAmenities(params?: Record<string, any>): Promise<PaginatedResponse<Amenity>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params
      ? `${API_ENDPOINTS.PROJECTS.AMENITIES_LIST}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.AMENITIES_LIST;
    
    return apiClient.get<PaginatedResponse<Amenity>>(endpoint);
  },

  /**
   * Get amenity by ID
   */
  async getAmenityById(id: string): Promise<Amenity> {
    return apiClient.get<Amenity>(API_ENDPOINTS.PROJECTS.AMENITY_DETAIL(id));
  },

  /**
   * Create new amenity
   */
  async createAmenity(data: Partial<Amenity>): Promise<Amenity> {
    return apiClient.post<Amenity>(API_ENDPOINTS.PROJECTS.AMENITIES_LIST, data);
  },

  /**
   * Update amenity
   */
  async updateAmenity(id: string, data: Partial<Amenity>): Promise<Amenity> {
    return apiClient.patch<Amenity>(API_ENDPOINTS.PROJECTS.AMENITY_DETAIL(id), data);
  },

  /**
   * Delete amenity
   */
  async deleteAmenity(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.AMENITY_DETAIL(id));
  },

  /**
   * Get amenities statistics
   * Returns total count, counts by category, status, and parent_type
   */
  async getAmenitiesStats(params?: Record<string, any>): Promise<AmenitiesStatsResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const endpoint = queryParams.toString()
      ? `/projects/amenities/stats/?${queryParams.toString()}`
      : '/projects/amenities/stats/';
    return apiClient.get<AmenitiesStatsResponse>(endpoint);
  },

  /**
   * Bulk update category for multiple amenities
   */
  async bulkUpdateAmenityCategory(data: {
    amenity_ids: string[];
    category_id: string | null;
  }): Promise<BulkAmenityActionResponse> {
    return apiClient.post<BulkAmenityActionResponse>('/projects/amenities/bulk-update-category/', data);
  },

  /**
   * Bulk update status for multiple amenities
   */
  async bulkUpdateAmenityStatus(data: {
    amenity_ids: string[];
    status_id: string | null;
  }): Promise<BulkAmenityActionResponse> {
    return apiClient.post<BulkAmenityActionResponse>('/projects/amenities/bulk-update-status/', data);
  },

  /**
   * Bulk delete amenities (soft delete)
   */
  async bulkDeleteAmenities(data: {
    amenity_ids: string[];
  }): Promise<{ success: boolean; deleted_count: number; message: string }> {
    return apiClient.post<{ success: boolean; deleted_count: number; message: string }>('/projects/amenities/bulk-delete/', data);
  },

  // ============================================
  // ORBIT VIEWS
  // ============================================
  
  /**
   * Get all orbit views
   */
  async getOrbitViews(params?: Record<string, any>): Promise<PaginatedResponse<OrbitView>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.ORBIT_VIEWS}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.ORBIT_VIEWS;
    
    return apiClient.get<PaginatedResponse<OrbitView>>(endpoint);
  },

  /**
   * Get orbit view by ID
   */
  async getOrbitViewById(id: string): Promise<OrbitView> {
    return apiClient.get<OrbitView>(API_ENDPOINTS.PROJECTS.ORBIT_VIEW_DETAIL(id));
  },

  /**
   * Create new orbit view
   */
  async createOrbitView(data: Partial<OrbitView>): Promise<OrbitView> {
    return apiClient.post<OrbitView>(API_ENDPOINTS.PROJECTS.ORBIT_VIEWS, data);
  },

  /**
   * Update orbit view
   */
  async updateOrbitView(id: string, data: Partial<OrbitView>): Promise<OrbitView> {
    return apiClient.patch<OrbitView>(API_ENDPOINTS.PROJECTS.ORBIT_VIEW_DETAIL(id), data);
  },

  /**
   * Delete orbit view
   */
  async deleteOrbitView(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.ORBIT_VIEW_DETAIL(id));
  },

  /**
   * Upload orbit view images (ZIP file) using presigned URL
   * This uploads directly to S3, bypassing the Django server for large files.
   *
   * Flow:
   * 1. Get presigned URL from backend
   * 2. Upload ZIP directly to S3 using presigned URL
   * 3. Tell backend to process the uploaded ZIP (and optionally auto-sync OrbitView)
   *
   * @param projectId - UUID of the project
   * @param folderName - Name for the images folder
   * @param zipFile - ZIP file containing the images
   * @param onProgress - Optional callback for upload progress (0-100)
   * @param orbitViewId - Optional OrbitView ID to auto-sync with detected config
   * @returns Upload result with folder info and file counts
   */
  async uploadOrbitViewImages(
    projectId: string,
    folderName: string,
    zipFile: File,
    onProgress?: (progress: number) => void,
    orbitViewId?: string
  ): Promise<{
    success: boolean;
    folder_name: string;
    s3_path: string;
    root_files: number;
    low_files: number;
    detected_extension: string;
    uploaded_files: {
      root: string[];
      low: string[];
    };
    warnings?: string[];
    orbit_view_updated?: boolean;
  }> {
    // Step 1: Get presigned URL from backend
    const presignedResponse = await apiClient.post<{
      upload_url: string;
      s3_key: string;
      folder_name: string;
      expires_in: number;
    }>('/projects/orbit-views/get-upload-url/', {
      project_id: projectId,
      folder_name: folderName,
    });

    // Step 2: Upload ZIP directly to S3 using presigned URL
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}: ${xhr.responseText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during S3 upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', presignedResponse.upload_url);
        xhr.setRequestHeader('Content-Type', 'application/zip');
        xhr.send(zipFile);
      });

    } catch (error) {
      throw error;
    }

    // Step 3: Tell backend to process the uploaded ZIP
    // This can take a long time for large ZIPs (100+ images), so use 10 minute timeout
    const processResponse = await apiClient.post<{
      success: boolean;
      folder_name: string;
      s3_path: string;
      root_files: number;
      low_files: number;
      detected_extension: string;
      uploaded_files: {
        root: string[];
        low: string[];
      };
      warnings?: string[];
    }>('/projects/orbit-views/process-uploaded-zip/', {
      project_id: projectId,
      s3_key: presignedResponse.s3_key,
      folder_name: folderName,
      ...(orbitViewId && { orbit_view_id: orbitViewId }),
    }, {
      timeout: 600000, // 10 minutes for processing large ZIPs
    });

    return processResponse;
  },

  /**
   * List all image folders for a project
   * @param projectId - UUID of the project
   * @returns List of image folders with file counts
   */
  async listOrbitViewImageFolders(projectId: string): Promise<{
    folders: Array<{
      id: string;
      name: string;
      rootFiles: number;
      lowFiles: number;
      extension?: string;
    }>;
  }> {
    return apiClient.get(`/projects/orbit-views/image-folders/${projectId}/`);
  },

  /**
   * Delete an image folder from S3
   * @param projectId - UUID of the project
   * @param folderName - Name of the folder to delete
   * @returns Success message with deleted file count
   */
  async deleteOrbitViewImageFolder(projectId: string, folderName: string): Promise<{
    success: boolean;
    message: string;
    deleted_count: number;
  }> {
    return apiClient.delete(`/projects/orbit-views/image-folders/${projectId}/${folderName}/`);
  },

  /**
   * Upload a GLB model file for an OrbitView
   * Uses presigned URL for direct S3 upload.
   *
   * @param orbitViewId - UUID of the OrbitView
   * @param modelType - Type of model: 'main', 'amenities', or 'stages'
   * @param glbFile - The GLB file to upload
   * @param onProgress - Optional callback for upload progress (0-100)
   * @returns Upload result with updated orbit view data
   */
  async uploadOrbitViewModel(
    orbitViewId: string,
    modelType: 'main' | 'amenities' | 'stages',
    glbFile: File,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    message: string;
    orbit_view: any;
    s3_key: string;
    model_path: string;
  }> {
    // Step 1: Get presigned URL from backend
    const presignedResponse = await apiClient.post<{
      upload_url: string;
      s3_key: string;
      model_path: string;
      model_type: string;
      expires_in: number;
    }>('/projects/orbit-views/upload-model/', {
      orbit_view_id: orbitViewId,
      model_type: modelType,
      filename: glbFile.name,
    });

    // Step 2: Upload GLB directly to S3 using presigned URL
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}: ${xhr.responseText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during S3 upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', presignedResponse.upload_url);
        xhr.setRequestHeader('Content-Type', 'model/gltf-binary');
        // Note: ACL headers not supported - bucket uses bucket policy for public access
        xhr.send(glbFile);
      });

    } catch (error) {
      throw error;
    }

    // Step 3: Confirm upload to backend (updates database)
    const confirmResponse = await apiClient.post<{
      success: boolean;
      message: string;
      orbit_view: any;
    }>('/projects/orbit-views/confirm-model-upload/', {
      orbit_view_id: orbitViewId,
      model_type: modelType,
      model_path: presignedResponse.model_path,
    });

    // Include s3_key for URL construction
    return {
      ...confirmResponse,
      s3_key: presignedResponse.s3_key,
      model_path: presignedResponse.model_path,
    };
  },

  // ============================================
  // MULTI-GLB UPLOAD (Stage-specific models)
  // ============================================

  /**
   * Upload GLB file with type specification for multi-GLB support.
   *
   * Supports three GLB types:
   * - stages_mask: Mask showing project stages (stagesMask.glb)
   * - main: Main apartments model for single-stage projects (main.glb)
   * - stage_apartments: Apartments model for a specific stage
   *
   * @param orbitViewId - UUID of the OrbitView
   * @param glbType - Type of GLB being uploaded
   * @param glbFile - The GLB file to upload
   * @param stageId - Required when glbType is 'stage_apartments'
   * @param onProgress - Progress callback (0-100)
   * @param imagesConfig - Optional per-stage images configuration
   */
  async uploadGLBWithType(
    orbitViewId: string,
    glbType: 'stages_mask' | 'main' | 'stage_apartments' | 'amenities_mask',
    glbFile: File,
    stageId?: string,
    onProgress?: (progress: number) => void,
    imagesConfig?: {
      imagesFolder?: string;
      numImages?: number;
      imagesExtension?: string;
    }
  ): Promise<{
    success: boolean;
    glb_type: string;
    path: string;
    url: string;
    orbit_view_type: 'legacy' | 'single_stage' | 'multi_stage';
    available_stages: Array<{
      id: string;
      name: string;
      code: string;
      stage_number: string;
      has_apartments_model: boolean;
      model_path: string | null;
      uploaded_at: string | null;
      images_folder?: string | null;
      num_images?: number | null;
      images_extension?: string | null;
      file_name?: string | null;
      file_size?: number | null;
    }>;
    detected_stages?: string[];
    detected_entities?: {
      stages: Array<{
        code: string;
        full_code: string;
        exists_in_db: boolean;
        stage_id?: string;
        stage_name: string;
      }>;
      blocks: Array<{
        key: string;
        stage_code: string;
        block_code: string;
        full_stage_code: string;
        full_block_code: string;
        exists_in_db: boolean;
        block_id?: string;
        block_name: string;
      }>;
      missing_stages_count: number;
      missing_blocks_count: number;
      requires_entity_creation: boolean;
    };
    matching_summary?: {
      mapping_id?: string;
      total_meshes?: number;
      unit_meshes?: number;
      stage_meshes?: number;
      block_meshes?: number;
      matched?: number;
      unmatched?: number;
      match_rate?: number;
      glb_codes_updated?: number;
      glb_codes_updated_details?: Array<{
        mesh_name: string;
        entity_type: string;
        entity_id: string;
      }>;
      stages_glb_codes_updated?: Array<{
        stage_id: string;
        stage_name: string;
        glb_code: string;
      }>;
      error?: string;
      status?: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', glbFile);
    formData.append('glb_type', glbType);
    if (stageId) {
      formData.append('stage_id', stageId);
    }
    // Per-stage images configuration
    if (imagesConfig?.imagesFolder) {
      formData.append('images_folder', imagesConfig.imagesFolder);
    }
    if (imagesConfig?.numImages) {
      formData.append('num_images', String(imagesConfig.numImages));
    }
    if (imagesConfig?.imagesExtension) {
      formData.append('images_extension', imagesConfig.imagesExtension);
    }

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error || `Upload failed: ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('POST', `${API_BASE_URL}/api/projects/orbit-views/${orbitViewId}/upload-glb/`);

      // Add auth header - use firebase_token (same as api-client.ts)
      const token = localStorage.getItem('firebase_token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  },

  /**
   * Delete a stage-specific GLB file from an OrbitView.
   *
   * @param orbitViewId - UUID of the OrbitView
   * @param stageId - UUID of the stage whose GLB to delete
   */
  async deleteStageGLB(
    orbitViewId: string,
    stageId: string
  ): Promise<{
    success: boolean;
    deleted_stage_id: string;
    orbit_view_type: string;
  }> {
    return apiClient.delete(`/projects/orbit-views/${orbitViewId}/delete-stage-glb/${stageId}/`);
  },

  // ============================================
  // GLB MAPPING (Mesh Analysis & Entity Creation)
  // ============================================

  /**
   * Analyze a GLB file and extract mesh information
   * @param orbitViewId - UUID of the OrbitView
   * @param glbUrl - URL to the GLB file (e.g., S3 URL)
   * @returns Analysis results with mesh counts and mapping ID
   */
  async analyzeGLBModel(orbitViewId: string, glbUrl: string): Promise<{
    mapping_id: string;
    status: string;
    analysis: {
      total_meshes: number;
      unit_meshes: number;
      amenity_meshes: number;
      decoration_meshes: number;
      unknown_meshes: number;
      unique_stages: number;
      unique_blocks: number;
      unique_typologies: number;
    };
  }> {
    return apiClient.post('/projects/glb-mappings/analyze/', {
      orbit_view_id: orbitViewId,
      glb_url: glbUrl,
    });
  },

  /**
   * Get GLB mapping details
   * @param mappingId - UUID of the GLBMapping
   */
  async getGLBMapping(mappingId: string): Promise<{
    id: string;
    orbit_view_id: string;
    orbit_view_name: string;
    project_name: string;
    glb_file_name: string;
    status: string;
    total_meshes: number;
    unit_meshes: number;
    amenity_meshes: number;
    matched_meshes: number;
    unmatched_meshes: number;
    created_entities: number;
    match_rate: number;
  }> {
    return apiClient.get(`/projects/glb-mappings/${mappingId}/`);
  },

  /**
   * Get all mesh entries for a GLB mapping
   * @param mappingId - UUID of the GLBMapping
   * @param filters - Optional filters (entity_type, match_status)
   */
  async getGLBMeshes(mappingId: string, filters?: {
    entity_type?: 'unit' | 'amenity' | 'decoration';
    match_status?: 'pending' | 'matched' | 'unmatched' | 'created';
  }): Promise<{
    mapping_id: string;
    total: number;
    meshes: Array<{
      id: string;
      mesh_name: string;
      mesh_index: number;
      entity_type: string;
      match_status: string;
      match_confidence: number;
      match_method: string;
      parsed_data: {
        stage?: string;
        block?: string;
        unit?: string;
        typology?: string;
        amenity?: string;
      };
      matched_entity?: {
        type: string;
        id: string;
        name: string;
        code: string;
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (filters?.entity_type) params.append('entity_type', filters.entity_type);
    if (filters?.match_status) params.append('match_status', filters.match_status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiClient.get(`/projects/glb-mappings/${mappingId}/meshes/${query}`);
  },

  /**
   * Run automatic matching for all meshes in a GLB mapping
   * This also updates glb_code on matched entities
   * @param mappingId - UUID of the GLBMapping
   * @param options - Optional matching options
   */
  async runGLBMatching(mappingId: string, options?: {
    /** Target stage ID - CRITICAL for stage_apartments to prevent cross-stage matching */
    target_stage_id?: string;
  }): Promise<{
    mapping_id: string;
    status: string;
    matched: number;
    unmatched: number;
    match_rate: number;
    results: Array<{
      mesh_name: string;
      status: string;
      confidence: number;
      method: string;
      entity_type: string;
      entity_id: string | null;
    }>;
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/match/`, options || {});
  },

  /**
   * Preview what entities would be created from unmatched meshes
   * @param mappingId - UUID of the GLBMapping
   */
  async previewGLBCreation(mappingId: string): Promise<{
    mapping_id: string;
    preview: {
      stages: string[];
      blocks: string[];
      typologies: string[];
      units: Array<{
        mesh_name: string;
        unit_code: string;
        stage_code: string;
        block_code: string;
        typology_code: string;
      }>;
      amenities: Array<{
        mesh_name: string;
        slug: string;
      }>;
    };
    counts: {
      stages: number;
      blocks: number;
      typologies: number;
      units: number;
      amenities: number;
    };
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/preview/`);
  },

  /**
   * Create entities from unmatched meshes
   * @param mappingId - UUID of the GLBMapping
   * @param options - Creation options
   */
  async createGLBEntities(mappingId: string, options?: {
    create_stages?: boolean;
    create_blocks?: boolean;
    create_units?: boolean;
    create_typologies?: boolean;
    create_amenities?: boolean;
    default_unit_status?: 'available' | 'reserved' | 'sold' | 'blocked';
    /** Target stage ID - CRITICAL for stage_apartments to ensure entities are created in correct stage */
    target_stage_id?: string;
  }): Promise<{
    mapping_id: string;
    status: string;
    created: Array<{ type: string; id: string; name: string; code: string }>;
    updated: Array<{ type: string; id: string; code: string }>;
    skipped: Array<{ type: string; code: string; reason: string }>;
    errors: Array<{ type: string; code: string; error: string }>;
    counts: {
      stages: number;
      blocks: number;
      typologies: number;
      units: number;
      amenities: number;
    };
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/create-entities/`, options || {});
  },

  /**
   * Apply matches - update unit codes and optionally typologies to match GLB mesh names
   * @param mappingId - UUID of the GLBMapping
   * @param options - Apply options
   */
  async applyGLBMatches(mappingId: string, options?: {
    apply_code_updates?: boolean;
    apply_typology_changes?: boolean;
    create_missing_typologies?: boolean;
    confirmed_entity_ids?: string[];
    dry_run?: boolean;
  }): Promise<{
    mapping_id: string;
    status: string;
    applied: Array<{
      entity_type: string;
      entity_id: string;
      change_type: string;
      old_value: string;
      new_value: string;
    }>;
    skipped: Array<{
      entity_type: string;
      entity_id: string;
      reason: string;
    }>;
    errors: Array<{
      entity_type: string;
      entity_id: string;
      error: string;
    }>;
    stats: {
      code_updates: number;
      typology_changes: number;
      errors: number;
    };
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/apply-matches/`, options || {});
  },

  /**
   * Get GLB naming convention for a project
   * @param projectId - UUID of the Project
   */
  async getGLBConvention(projectId: string): Promise<{
    project_id: string;
    stage_prefix: string;
    block_prefix: string;
    unit_prefix: string;
    typology_prefix: string;
    amenity_prefix: string;
    separator: string;
    require_stage: boolean;
    require_block: boolean;
    require_typology: boolean;
    custom_pattern: string;
    alias_mappings: Record<string, string>;
  }> {
    return apiClient.get(`/projects/glb-mappings/conventions/${projectId}/`);
  },

  /**
   * Update GLB naming convention for a project
   * @param projectId - UUID of the Project
   * @param convention - Convention settings to update
   */
  async updateGLBConvention(projectId: string, convention: {
    stage_prefix?: string;
    block_prefix?: string;
    unit_prefix?: string;
    typology_prefix?: string;
    amenity_prefix?: string;
    separator?: string;
    require_stage?: boolean;
    require_block?: boolean;
    require_typology?: boolean;
    custom_pattern?: string;
    alias_mappings?: Record<string, string>;
  }): Promise<{
    project_id: string;
    updated: boolean;
  }> {
    return apiClient.put(`/projects/glb-mappings/conventions/${projectId}/`, convention);
  },

  /**
   * Detect the current state of a project's structure.
   * Returns whether the project has existing entities and orbit views.
   * @param projectId - UUID of the Project
   */
  async detectProjectState(projectId: string): Promise<{
    project_id: string;
    project_name: string;
    has_structure: boolean;
    has_orbit_views: boolean;
    suggested_scenario: 'from_scratch' | 'link_existing' | 'update_or_replace';
    suggested_action: string;
    counts: {
      stages: number;
      blocks: number;
      units: number;
      typologies: number;
      orbit_views: number;
    };
    orbit_views: Array<{
      id: string;
      name: string;
      type: string;
      has_glb: boolean;
      has_images: boolean;
    }>;
    entities: {
      stages: Array<{
        id: string;
        name: string;
        code: string;
        glb_code: string;
        blocks_count: number;
      }>;
      blocks: Array<{
        id: string;
        name: string;
        code: string;
        glb_code: string;
        stage_name: string;
        stage_code: string;
        units_count: number;
      }>;
      typologies: Array<{
        id: string;
        name: string;
        code: string;
        glb_code: string;
        stage_name: string | null;
      }>;
    };
  }> {
    return apiClient.get(`/projects/glb-mappings/detect-state/${projectId}/`);
  },

  /**
   * Compare analyzed GLB meshes with existing project structure.
   * Returns a detailed DIFF showing matches, new items, missing items, and changes.
   * @param mappingId - UUID of the GLBMapping (from analyzeGLBModel)
   */
  async compareGLBWithExisting(mappingId: string): Promise<{
    mapping_id: string;
    comparison: {
      matches: Array<{
        mesh_name: string;
        entity_type: string;
        entity_id: string;
        entity_code: string;
        entity_name: string | null;
        match_method: string;
        action: 'link';
      }>;
      new_in_glb: Array<{
        mesh_name: string;
        entity_type: string;
        parsed: {
          stage?: string;
          block?: string;
          unit?: string;
          typology?: string;
        };
        suggested_action: 'create';
        dependencies: string[];
      }>;
      missing_in_glb: Array<{
        entity_type: string;
        entity_id: string;
        entity_code: string;
        entity_name: string;
        block_name?: string;
        stage_name?: string;
        warning: 'not_visible';
        message: string;
      }>;
      changes_detected: Array<{
        mesh_name: string;
        entity_type: string;
        entity_id: string;
        entity_code: string;
        change_type: 'glb_code_changed' | 'typology_changed' | 'block_changed';
        old_value: string;
        new_value: string;
        requires_confirmation: boolean;
      }>;
    };
    stats: {
      total_meshes: number;
      matched: number;
      new_in_glb: number;
      missing_in_glb: number;
      changes_detected: number;
      match_rate: number;
    };
    has_changes_requiring_confirmation: boolean;
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/compare/`);
  },

  /**
   * Preview synchronization between GLB meshes and database entities.
   * Returns categorized results for user confirmation before applying changes.
   *
   * @param mappingId - UUID of the GLBMapping
   * @param targetStageId - Optional UUID of target stage (for stage_apartments)
   * @returns Preview of what will be matched, created, orphaned, or conflicted
   */
  async syncPreview(
    mappingId: string,
    targetStageId?: string
  ): Promise<{
    mapping_id: string;
    target_stage: {
      id: string;
      name: string;
      code: string;
    } | null;
    sync_preview: {
      matched: Array<{
        mesh_name: string;
        entity_id: string;
        entity_code: string;
        entity_name: string;
        block_name: string | null;
        stage_name: string | null;
        match_method: string;
        pending_updates: Array<{
          type: string;
          field: string;
          current: string;
          new: string;
          requires_confirmation: boolean;
        }>;
      }>;
      to_create: Array<{
        mesh_name: string;
        parsed: Record<string, string>;
        suggested_unit_number: string;
        suggested_block: string;
        suggested_stage: string;
        suggested_typology: string;
      }>;
      orphaned: Array<{
        entity_id: string;
        entity_code: string;
        entity_name: string;
        unit_number: string;
        block_name: string | null;
        stage_name: string | null;
        status: string;
        status_display: string;
        is_protected: boolean;
        protection_reason: string | null;
        warning: string;
        message: string;
      }>;
      conflicts: Array<{
        mesh_name: string;
        entity_id: string;
        entity_code: string;
        entity_name: string;
        block_name: string | null;
        stage_name: string | null;
        match_method: string;
        conflict_details: Array<{
          type: string;
          field: string;
          current: string;
          new: string;
          requires_confirmation: boolean;
        }>;
      }>;
    };
    counts: {
      total_glb_meshes: number;
      total_db_units: number;
      matched: number;
      to_create: number;
      orphaned: number;
      orphaned_protected: number;
      conflicts: number;
    };
    summary: {
      can_auto_apply: boolean;
      has_protected_orphans: boolean;
      requires_confirmation: boolean;
    };
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/sync-preview/`, {
      target_stage_id: targetStageId,
    });
  },

  /**
   * Execute synchronization based on user-approved actions from sync-preview.
   * Creates missing entities and applies matches.
   *
   * @param mappingId - UUID of the GLBMapping
   * @param options - Sync execution options
   * @returns Results of the sync execution
   */
  async syncExecute(
    mappingId: string,
    options: {
      target_stage_id?: string;
      create_missing?: boolean;
      apply_matches?: boolean;
      apply_conflicts?: boolean;
      dry_run?: boolean;
    }
  ): Promise<{
    success: boolean;
    dry_run: boolean;
    mapping_id: string;
    target_stage: {
      id: string;
      name: string;
    } | null;
    results: {
      created: Array<{
        mesh_name: string;
        entity_id?: string;
        entity_code: string;
        block_code?: string;
        typology_code?: string;
        dry_run?: boolean;
      }>;
      updated: Array<{
        mesh_name: string;
        entity_id: string;
        entity_code: string;
        changes: string[];
      }>;
      skipped: Array<{
        mesh_name: string;
        entity_id?: string;
        reason: string;
      }>;
      errors: Array<{
        mesh_name: string;
        error: string;
      }>;
    };
    stats: {
      created: number;
      updated: number;
      skipped: number;
      errors: number;
    };
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/sync-execute/`, options);
  },

  /**
   * Calculate target frames for all units in a GLB mapping.
   * Uses mesh centroid positions and camera animation data to determine
   * the optimal viewing frame for each unit in the orbit view.
   *
   * @param mappingId - UUID of the GLBMapping
   * @param options - Calculation options
   * @returns Summary of calculations performed
   */
  async calculateTargetFrames(
    mappingId: string,
    options?: {
      dry_run?: boolean;
      preserve_manual?: boolean;
      global_offset?: number;
      stage_id?: string;
    }
  ): Promise<{
    mapping_id: string;
    orbit_view_name: string;
    total_meshes: number;
    unit_meshes_found: number;
    calculated: number;
    updated: number;
    skipped_manual: number;
    skipped_no_geometry: number;
    skipped_no_unit: number;
    errors: number;
    camera_offset_deg: number;
    total_frames: number;
    results?: Array<{
      mesh_name: string;
      unit_id: string | null;
      unit_code: string | null;
      centroid: [number, number, number];
      angle_deg: number;
      calculated_frame: number;
      previous_frame: number | null;
      was_manual: boolean;
      updated: boolean;
      error: string | null;
    }>;
  }> {
    return apiClient.post(`/projects/glb-mappings/${mappingId}/calculate-frames/`, options || {});
  },

  // ============================================
  // PRICING SCHEMES (PRICE LISTS)
  // ============================================

  /**
   * Get all pricing schemes
   */
  async getPricingSchemes(params?: Record<string, any>): Promise<PaginatedResponse<PricingScheme>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `${API_ENDPOINTS.PROJECTS.PRICING_SCHEMES}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.PRICING_SCHEMES;

    return apiClient.get<PaginatedResponse<PricingScheme>>(endpoint);
  },

  /**
   * Get pricing scheme by ID
   */
  async getPricingSchemeById(id: string): Promise<PricingScheme> {
    return apiClient.get<PricingScheme>(API_ENDPOINTS.PROJECTS.PRICING_SCHEME_DETAIL(id));
  },

  /**
   * Create new pricing scheme
   */
  async createPricingScheme(data: Partial<PricingScheme>): Promise<PricingScheme> {
    return apiClient.post<PricingScheme>(API_ENDPOINTS.PROJECTS.PRICING_SCHEMES, data);
  },

  /**
   * Update pricing scheme
   */
  async updatePricingScheme(id: string, data: Partial<PricingScheme>): Promise<PricingScheme> {
    return apiClient.patch<PricingScheme>(API_ENDPOINTS.PROJECTS.PRICING_SCHEME_DETAIL(id), data);
  },

  /**
   * Delete pricing scheme
   */
  async deletePricingScheme(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.PRICING_SCHEME_DETAIL(id));
  },

  // ============================================
  // UNITS
  // ============================================
  
  /**
   * Get all units
   */
  async getUnits(params?: Record<string, any>): Promise<PaginatedResponse<Unit>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.PROJECTS.UNITS_BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.PROJECTS.UNITS_BASE;
    
    return apiClient.get<PaginatedResponse<Unit>>(endpoint);
  },

  /**
   * Get unit by ID
   */
  async getUnitById(id: string): Promise<Unit> {
    return apiClient.get<Unit>(API_ENDPOINTS.PROJECTS.UNIT_DETAIL(id));
  },

  /**
   * Create new unit
   */
  async createUnit(data: Partial<Unit>): Promise<Unit> {
    return apiClient.post<Unit>(API_ENDPOINTS.PROJECTS.UNITS_BASE, data);
  },

  /**
   * Update unit
   */
  async updateUnit(id: string, data: Partial<Unit>): Promise<Unit> {
    return apiClient.patch<Unit>(API_ENDPOINTS.PROJECTS.UNIT_DETAIL(id), data);
  },

  /**
   * Delete unit
   */
  async deleteUnit(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.PROJECTS.UNIT_DETAIL(id));
  },

  /**
   * Reserve unit
   * @param id - Unit ID
   * @param leadId - Lead ID (optional if skipLeadValidation is true)
   * @param skipLeadValidation - If true, allows reserving without a lead
   */
  async reserveUnit(id: string, leadId: string, skipLeadValidation?: boolean): Promise<Unit> {
    const payload: { lead_id?: string; skip_lead_validation?: boolean } = {};
    if (leadId) {
      payload.lead_id = leadId;
    }
    if (skipLeadValidation) {
      payload.skip_lead_validation = skipLeadValidation;
    }
    return apiClient.post<Unit>(
      API_ENDPOINTS.PROJECTS.UNIT_RESERVE(id),
      payload
    );
  },

  /**
   * Mark unit as sold
   */
  async markUnitAsSold(id: string, soldTo?: string): Promise<Unit> {
    return apiClient.post<Unit>(
      API_ENDPOINTS.PROJECTS.UNIT_MARK_AS_SOLD(id),
      { sold_to: soldTo }
    );
  },

  /**
   * Find similar units
   */
  async findSimilarUnits(params: {
    unit_type?: string;
    bedrooms?: number;
    price_range?: number;
  }): Promise<Unit[]> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get<PaginatedResponse<Unit>>(
      `${API_ENDPOINTS.PROJECTS.UNIT_SIMILAR}?${queryParams.toString()}`
    );
    return response.results || [];
  },

  /**
   * Calculate unit price based on pricing scheme configuration
   */
  async calculateUnitPrice(id: string, save: boolean = false): Promise<{
    success: boolean;
    total?: number;
    breakdown?: any;
    saved?: boolean;
    error?: string;
  }> {
    return apiClient.post<{
      success: boolean;
      total?: number;
      breakdown?: any;
      saved?: boolean;
      error?: string;
    }>(`${API_ENDPOINTS.PROJECTS.UNITS_BASE}${id}/calculate_price/`, { save });
  },

  /**
   * Calculate prices for multiple units in bulk
   */
  async bulkCalculateUnitPrices(unitIds: string[], save: boolean = false): Promise<{
    success: boolean;
    results: Array<{
      unit_id: string;
      unit_number: string;
      success: boolean;
      total?: number;
      breakdown?: any;
      error?: string;
    }>;
    saved: boolean;
    total_units: number;
    successful: number;
  }> {
    return apiClient.post<{
      success: boolean;
      results: Array<{
        unit_id: string;
        unit_number: string;
        success: boolean;
        total?: number;
        breakdown?: any;
        error?: string;
      }>;
      saved: boolean;
      total_units: number;
      successful: number;
    }>(`${API_ENDPOINTS.PROJECTS.UNITS_BASE}bulk_calculate_prices/`, {
      unit_ids: unitIds,
      save
    });
  },

  async bulkAdjustPrice(data: {
    project_id: string;
    stage_id?: string | null;
    adjustment_type: 'percentage' | 'absolute';
    adjustment_value: number;
  }): Promise<{
    success: boolean;
    units_updated: number;
    units_skipped: number;
    summary: { avg_old_price: number; avg_new_price: number; total_adjustment: number };
  }> {
    return apiClient.post(`${API_ENDPOINTS.PROJECTS.UNITS_BASE}bulk_adjust_price/`, data);
  },

  // ============================================
  // AMENITY CATEGORIES
  // ============================================

  /**
   * Get all amenity categories
   */
  async getAmenityCategories(): Promise<AmenityCategory[]> {
    const response = await apiClient.get<PaginatedResponse<AmenityCategory>>('/projects/amenity-categories/');
    return response.results || response || [];
  },

  /**
   * Create amenity category
   */
  async createAmenityCategory(data: Partial<AmenityCategory>): Promise<AmenityCategory> {
    return apiClient.post<AmenityCategory>('/projects/amenity-categories/', data);
  },

  /**
   * Update amenity category
   */
  async updateAmenityCategory(id: string, data: Partial<AmenityCategory>): Promise<AmenityCategory> {
    return apiClient.patch<AmenityCategory>(`/projects/amenity-categories/${id}/`, data);
  },

  /**
   * Delete amenity category
   */
  async deleteAmenityCategory(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/amenity-categories/${id}/`);
  },

  // ============================================
  // AMENITY STATUSES
  // ============================================

  /**
   * Get all amenity statuses
   */
  async getAmenityStatuses(): Promise<AmenityStatus[]> {
    const response = await apiClient.get<PaginatedResponse<AmenityStatus>>('/projects/amenity-statuses/');
    return response.results || response || [];
  },

  /**
   * Create amenity status
   */
  async createAmenityStatus(data: Partial<AmenityStatus>): Promise<AmenityStatus> {
    return apiClient.post<AmenityStatus>('/projects/amenity-statuses/', data);
  },

  /**
   * Update amenity status
   */
  async updateAmenityStatus(id: string, data: Partial<AmenityStatus>): Promise<AmenityStatus> {
    return apiClient.patch<AmenityStatus>(`/projects/amenity-statuses/${id}/`, data);
  },

  /**
   * Delete amenity status
   */
  async deleteAmenityStatus(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/amenity-statuses/${id}/`);
  },

  // ============================================
  // VIEW PRICE ADJUSTMENTS
  // ============================================

  /**
   * Get all view price adjustments
   */
  async getViewPriceAdjustments(params?: Record<string, any>): Promise<PaginatedResponse<ViewPriceAdjustment>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `/projects/view-price-adjustments/?${queryParams.toString()}`
      : '/projects/view-price-adjustments/';

    return apiClient.get<PaginatedResponse<ViewPriceAdjustment>>(endpoint);
  },

  /**
   * Get view price adjustment by ID
   */
  async getViewPriceAdjustmentById(id: string): Promise<ViewPriceAdjustment> {
    return apiClient.get<ViewPriceAdjustment>(`/projects/view-price-adjustments/${id}/`);
  },

  /**
   * Get view price adjustments by reference (project, stage, or pricing scheme)
   */
  async getViewPriceAdjustmentsByReference(
    applicability: 'project' | 'stage' | 'pricing_scheme',
    referenceId: string
  ): Promise<ViewPriceAdjustmentListItem[]> {
    return apiClient.get<ViewPriceAdjustmentListItem[]>(
      `/projects/view-price-adjustments/by_reference/?applicability=${applicability}&reference_id=${referenceId}`
    );
  },

  /**
   * Create new view price adjustment
   */
  async createViewPriceAdjustment(data: Partial<ViewPriceAdjustment>): Promise<ViewPriceAdjustment> {
    return apiClient.post<ViewPriceAdjustment>('/projects/view-price-adjustments/', data);
  },

  /**
   * Update view price adjustment
   */
  async updateViewPriceAdjustment(
    id: string,
    data: Partial<ViewPriceAdjustment>
  ): Promise<ViewPriceAdjustment> {
    return apiClient.patch<ViewPriceAdjustment>(`/projects/view-price-adjustments/${id}/`, data);
  },

  /**
   * Delete view price adjustment
   */
  async deleteViewPriceAdjustment(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/view-price-adjustments/${id}/`);
  },

  /**
   * Calculate price adjustment for a given base price
   */
  async calculateViewPriceAdjustment(
    adjustmentId: string,
    basePrice: number
  ): Promise<{
    base_price: number;
    adjustment_amount: number;
    final_price: number;
    adjustment_type: string;
    adjustment_value: number;
  }> {
    return apiClient.post(
      `/projects/view-price-adjustments/${adjustmentId}/calculate/`,
      { base_price: basePrice }
    );
  },

  // ============================================
  // PRICING SCHEME FILTERS (Helper Methods)
  // ============================================

  /**
   * Get pricing schemes for a specific unit (includes project and stage schemes)
   */
  async getPricingSchemesForUnit(unitId: string): Promise<PaginatedResponse<PricingScheme>> {
    return this.getPricingSchemes({ unit_id: unitId });
  },

  /**
   * Get pricing schemes for a specific project (includes all stage schemes)
   */
  async getPricingSchemesForProject(projectId: string): Promise<PaginatedResponse<PricingScheme>> {
    return this.getPricingSchemes({ project_id: projectId });
  },

  /**
   * Get pricing schemes for a specific stage
   */
  async getPricingSchemesForStage(stageId: string): Promise<PaginatedResponse<PricingScheme>> {
    return this.getPricingSchemes({ stage_id: stageId });
  },

  // ============================================
  // AREA TYPES (Configurable Area Types)
  // ============================================

  /**
   * Get all area types
   */
  async getAreaTypes(params?: Record<string, any>): Promise<PaginatedResponse<AreaType>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `/projects/area-types/?${queryParams.toString()}`
      : '/projects/area-types/';

    return apiClient.get<PaginatedResponse<AreaType>>(endpoint);
  },

  /**
   * Get area type by ID
   */
  async getAreaTypeById(id: string): Promise<AreaType> {
    return apiClient.get<AreaType>(`/projects/area-types/${id}/`);
  },

  /**
   * Create new area type
   */
  async createAreaType(data: Partial<AreaType>): Promise<AreaType> {
    return apiClient.post<AreaType>('/projects/area-types/', data);
  },

  /**
   * Update area type
   */
  async updateAreaType(id: string, data: Partial<AreaType>): Promise<AreaType> {
    return apiClient.patch<AreaType>(`/projects/area-types/${id}/`, data);
  },

  /**
   * Delete area type (soft delete - marks as inactive)
   */
  async deleteAreaType(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/area-types/${id}/`);
  },

  // ============================================
  // AREA TYPE SUBCATEGORIES
  // ============================================

  /**
   * Get all subcategories for an area type
   */
  async getAreaTypeSubcategories(areaTypeId: string, params?: Record<string, any>): Promise<AreaTypeSubcategory[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const endpoint = `/projects/area-type-subcategories/by-area-type/${areaTypeId}/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<AreaTypeSubcategory[]>(endpoint);
  },

  /**
   * Get subcategory by ID
   */
  async getAreaTypeSubcategoryById(id: string): Promise<AreaTypeSubcategory> {
    return apiClient.get<AreaTypeSubcategory>(`/projects/area-type-subcategories/${id}/`);
  },

  /**
   * Create new subcategory for an area type
   */
  async createAreaTypeSubcategory(data: Partial<AreaTypeSubcategory>): Promise<AreaTypeSubcategory> {
    return apiClient.post<AreaTypeSubcategory>('/projects/area-type-subcategories/', data);
  },

  /**
   * Update subcategory
   */
  async updateAreaTypeSubcategory(id: string, data: Partial<AreaTypeSubcategory>): Promise<AreaTypeSubcategory> {
    return apiClient.patch<AreaTypeSubcategory>(`/projects/area-type-subcategories/${id}/`, data);
  },

  /**
   * Delete subcategory
   */
  async deleteAreaTypeSubcategory(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/area-type-subcategories/${id}/`);
  },

  // ============================================
  // PROJECT AREA TYPE SUBCATEGORIES (Project-Specific)
  // ============================================

  /**
   * Get all subcategories for a project area type
   */
  async getProjectAreaTypeSubcategories(projectAreaTypeId: string, params?: Record<string, any>): Promise<ProjectAreaTypeSubcategory[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const endpoint = `/projects/project-area-type-subcategories/by-project-area-type/${projectAreaTypeId}/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiClient.get<ProjectAreaTypeSubcategory[]>(endpoint);
  },

  /**
   * Get project subcategory by ID
   */
  async getProjectAreaTypeSubcategoryById(id: string): Promise<ProjectAreaTypeSubcategory> {
    return apiClient.get<ProjectAreaTypeSubcategory>(`/projects/project-area-type-subcategories/${id}/`);
  },

  /**
   * Create new project-specific subcategory
   */
  async createProjectAreaTypeSubcategory(data: Partial<ProjectAreaTypeSubcategory>): Promise<ProjectAreaTypeSubcategory> {
    return apiClient.post<ProjectAreaTypeSubcategory>('/projects/project-area-type-subcategories/', data);
  },

  /**
   * Update project-specific subcategory
   */
  async updateProjectAreaTypeSubcategory(id: string, data: Partial<ProjectAreaTypeSubcategory>): Promise<ProjectAreaTypeSubcategory> {
    return apiClient.patch<ProjectAreaTypeSubcategory>(`/projects/project-area-type-subcategories/${id}/`, data);
  },

  /**
   * Delete project-specific subcategory
   */
  async deleteProjectAreaTypeSubcategory(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/project-area-type-subcategories/${id}/`);
  },

  // ============================================
  // PRICING SCHEME AREA PRICES
  // ============================================

  /**
   * Get all pricing scheme area prices
   */
  async getPricingSchemeAreaPrices(params?: Record<string, any>): Promise<PaginatedResponse<PricingSchemeAreaPrice>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `/projects/pricing-scheme-area-prices/?${queryParams.toString()}`
      : '/projects/pricing-scheme-area-prices/';

    return apiClient.get<PaginatedResponse<PricingSchemeAreaPrice>>(endpoint);
  },

  /**
   * Get pricing scheme area prices for a specific pricing scheme
   */
  async getPricingSchemeAreaPricesForScheme(pricingSchemeId: string): Promise<PaginatedResponse<PricingSchemeAreaPrice>> {
    if (!pricingSchemeId || pricingSchemeId === 'undefined' || pricingSchemeId === 'null') {
      console.warn('⚠️ getPricingSchemeAreaPricesForScheme called with invalid ID:', pricingSchemeId);
      return { results: [], count: 0, next: null, previous: null };
    }
    return this.getPricingSchemeAreaPrices({ pricing_scheme: pricingSchemeId });
  },

  /**
   * Get pricing scheme area price by ID
   */
  async getPricingSchemeAreaPriceById(id: string): Promise<PricingSchemeAreaPrice> {
    return apiClient.get<PricingSchemeAreaPrice>(`/projects/pricing-scheme-area-prices/${id}/`);
  },

  /**
   * Create new pricing scheme area price
   */
  async createPricingSchemeAreaPrice(data: Partial<PricingSchemeAreaPrice>): Promise<PricingSchemeAreaPrice> {
    return apiClient.post<PricingSchemeAreaPrice>('/projects/pricing-scheme-area-prices/', data);
  },

  /**
   * Update pricing scheme area price
   */
  async updatePricingSchemeAreaPrice(id: string, data: Partial<PricingSchemeAreaPrice>): Promise<PricingSchemeAreaPrice> {
    return apiClient.patch<PricingSchemeAreaPrice>(`/projects/pricing-scheme-area-prices/${id}/`, data);
  },

  /**
   * Delete pricing scheme area price
   */
  async deletePricingSchemeAreaPrice(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/pricing-scheme-area-prices/${id}/`);
  },

  // ============================================
  // UNIT AREA VALUES
  // ============================================

  /**
   * Get all unit area values
   */
  async getUnitAreaValues(params?: Record<string, any>): Promise<PaginatedResponse<UnitAreaValue>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `/projects/unit-area-values/?${queryParams.toString()}`
      : '/projects/unit-area-values/';

    return apiClient.get<PaginatedResponse<UnitAreaValue>>(endpoint);
  },

  /**
   * Get unit area values for a specific unit
   */
  async getUnitAreaValuesForUnit(unitId: string): Promise<PaginatedResponse<UnitAreaValue>> {
    return this.getUnitAreaValues({ unit: unitId });
  },

  /**
   * Create new unit area value
   */
  async createUnitAreaValue(data: Partial<UnitAreaValue>): Promise<UnitAreaValue> {
    return apiClient.post<UnitAreaValue>('/projects/unit-area-values/', data);
  },

  /**
   * Update unit area value
   */
  async updateUnitAreaValue(id: string, data: Partial<UnitAreaValue>): Promise<UnitAreaValue> {
    return apiClient.patch<UnitAreaValue>(`/projects/unit-area-values/${id}/`, data);
  },

  /**
   * Delete unit area value
   */
  async deleteUnitAreaValue(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/unit-area-values/${id}/`);
  },

  // ============================================
  // UNIT CUSTOM AREA PRICES
  // ============================================

  /**
   * Get all unit custom area prices
   */
  async getUnitCustomAreaPrices(params?: Record<string, any>): Promise<PaginatedResponse<UnitCustomAreaPrice>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `/projects/unit-custom-area-prices/?${queryParams.toString()}`
      : '/projects/unit-custom-area-prices/';

    return apiClient.get<PaginatedResponse<UnitCustomAreaPrice>>(endpoint);
  },

  /**
   * Get unit custom area prices for a specific unit
   */
  async getUnitCustomAreaPricesForUnit(unitId: string): Promise<PaginatedResponse<UnitCustomAreaPrice>> {
    return this.getUnitCustomAreaPrices({ unit: unitId });
  },

  /**
   * Create new unit custom area price
   */
  async createUnitCustomAreaPrice(data: Partial<UnitCustomAreaPrice>): Promise<UnitCustomAreaPrice> {
    return apiClient.post<UnitCustomAreaPrice>('/projects/unit-custom-area-prices/', data);
  },

  /**
   * Update unit custom area price
   */
  async updateUnitCustomAreaPrice(id: string, data: Partial<UnitCustomAreaPrice>): Promise<UnitCustomAreaPrice> {
    return apiClient.patch<UnitCustomAreaPrice>(`/projects/unit-custom-area-prices/${id}/`, data);
  },

  /**
   * Delete unit custom area price
   */
  async deleteUnitCustomAreaPrice(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/unit-custom-area-prices/${id}/`);
  },

  // ========================================
  // PRICING SCHEME PROGRESSION METHODS
  // ========================================

  /**
   * Get all pricing scheme progressions
   */
  async getPricingSchemeProgressions(params?: Record<string, any>): Promise<PaginatedResponse<PricingSchemeProgressionListItem>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, String(v)));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }

    const endpoint = queryParams.toString()
      ? `/projects/pricing-scheme-progressions/?${queryParams.toString()}`
      : '/projects/pricing-scheme-progressions/';

    return apiClient.get<PaginatedResponse<PricingSchemeProgressionListItem>>(endpoint);
  },

  /**
   * Get pricing scheme progression by ID
   */
  async getPricingSchemeProgressionById(id: string): Promise<PricingSchemeProgression> {
    return apiClient.get<PricingSchemeProgression>(`/projects/pricing-scheme-progressions/${id}/`);
  },

  /**
   * Get pricing scheme progressions for a specific stage
   */
  async getPricingSchemeProgressionsForStage(stageId: string): Promise<PaginatedResponse<PricingSchemeProgressionListItem>> {
    return this.getPricingSchemeProgressions({ stage: stageId });
  },

  /**
   * Create new pricing scheme progression
   */
  async createPricingSchemeProgression(data: Partial<PricingSchemeProgression>): Promise<PricingSchemeProgression> {
    return apiClient.post<PricingSchemeProgression>('/projects/pricing-scheme-progressions/', data);
  },

  /**
   * Update pricing scheme progression
   */
  async updatePricingSchemeProgression(id: string, data: Partial<PricingSchemeProgression>): Promise<PricingSchemeProgression> {
    return apiClient.patch<PricingSchemeProgression>(`/projects/pricing-scheme-progressions/${id}/`, data);
  },

  /**
   * Delete pricing scheme progression
   */
  async deletePricingSchemeProgression(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/pricing-scheme-progressions/${id}/`);
  },

  /**
   * Manually generate the next pricing scheme based on progression rules
   */
  async generateNextPriceList(
    progressionId: string,
    data?: GenerateNextPriceListRequest
  ): Promise<GenerateNextPriceListResponse> {
    return apiClient.post<GenerateNextPriceListResponse>(
      `/projects/pricing-scheme-progressions/${progressionId}/generate_next_price_list/`,
      data || {}
    );
  },

  /**
   * Check if the progression's trigger conditions are currently met
   */
  async checkProgressionTrigger(progressionId: string): Promise<CheckTriggerResponse> {
    return apiClient.get<CheckTriggerResponse>(
      `/projects/pricing-scheme-progressions/${progressionId}/check_trigger/`
    );
  },

  /**
   * Activate a pricing progression
   */
  async activateProgression(progressionId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/projects/pricing-scheme-progressions/${progressionId}/activate/`, {});
  },

  /**
   * Deactivate a pricing progression
   */
  async deactivateProgression(progressionId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/projects/pricing-scheme-progressions/${progressionId}/deactivate/`, {});
  },

  /**
   * Get execution history for a progression
   */
  async getProgressionHistory(progressionId: string): Promise<{
    history: Array<{
      timestamp: string;
      scheme_id: string;
      scheme_name: string;
      trigger_value: number;
      units_sold: number;
      sequence: number;
    }>;
    last_triggered: string | null;
    sequence_number: number;
    current_scheme: any;
    last_created_scheme: any;
  }> {
    return apiClient.get(`/projects/pricing-scheme-progressions/${progressionId}/history/`);
  },

  /**
   * Reset the progression's unit counter
   */
  async resetProgression(progressionId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/projects/pricing-scheme-progressions/${progressionId}/reset/`, {});
  },

  /**
   * Get all progressions for a specific stage using the by_stage endpoint
   */
  async getProgressionsByStage(stageId: string): Promise<PricingSchemeProgression[]> {
    return apiClient.get<PricingSchemeProgression[]>(`/projects/pricing-scheme-progressions/by_stage/?stage_id=${stageId}`);
  },

  // =============================================================================
  // Project Memberships API
  // =============================================================================

  /**
   * Get project memberships with optional filters
   */
  async getProjectMemberships(params?: {
    project?: string;
    user?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.project) queryParams.append('project', params.project);
    if (params?.user) queryParams.append('user', params.user);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    const url = `/projects/project-memberships/${queryParams.toString() ? `?${queryParams}` : ''}`;
    return apiClient.get(url);
  },

  /**
   * Get current user's projects with roles
   */
  async getMyProjectsWithRoles(): Promise<any[]> {
    return apiClient.get('/projects/project-memberships/my-projects/');
  },

  /**
   * Create a project membership
   */
  async createProjectMembership(data: {
    project: string;
    user: string;
    role: string;
    is_active?: boolean;
    is_primary?: boolean;
    notes?: string;
  }): Promise<any> {
    return apiClient.post('/projects/project-memberships/', data);
  },

  /**
   * Update a project membership
   */
  async updateProjectMembership(id: string, data: Partial<{
    role: string;
    is_active: boolean;
    is_primary: boolean;
    custom_permissions: Record<string, boolean>;
    notes: string;
  }>): Promise<any> {
    return apiClient.patch(`/projects/project-memberships/${id}/`, data);
  },

  /**
   * Delete a project membership
   */
  async deleteProjectMembership(id: string): Promise<void> {
    return apiClient.delete(`/projects/project-memberships/${id}/`);
  },

  /**
   * Bulk invite users to a project
   */
  async bulkInviteToProject(projectId: string, users: { user: string; role: string }[]): Promise<{
    created: any[];
    errors: { user: string; errors: any }[];
    created_count: number;
    error_count: number;
  }> {
    return apiClient.post('/projects/project-memberships/bulk-invite/', {
      project: projectId,
      users,
    });
  },

  /**
   * Set a project as the user's primary project
   */
  async setProjectAsPrimary(membershipId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/projects/project-memberships/${membershipId}/set-primary/`);
  },

  // =============================================================================
  // Project Modules API
  // =============================================================================

  /**
   * Get project modules with optional filters
   */
  async getProjectModules(params?: {
    project?: string;
    module?: string;
    is_enabled?: boolean;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.project) queryParams.append('project', params.project);
    if (params?.module) queryParams.append('module', params.module);
    if (params?.is_enabled !== undefined) queryParams.append('is_enabled', String(params.is_enabled));
    const url = `/projects/project-modules/${queryParams.toString() ? `?${queryParams}` : ''}`;
    return apiClient.get(url);
  },

  /**
   * Get all available modules
   */
  async getAvailableModules(): Promise<{ code: string; name: string; description: string }[]> {
    return apiClient.get('/projects/project-modules/available/');
  },

  /**
   * Create/enable a module for a project
   */
  async createProjectModule(data: {
    project: string;
    module: string;
    is_enabled?: boolean;
    config?: Record<string, any>;
    limits?: Record<string, number>;
    expires_at?: string;
  }): Promise<any> {
    return apiClient.post('/projects/project-modules/', data);
  },

  /**
   * Update a project module
   */
  async updateProjectModule(id: string, data: Partial<{
    is_enabled: boolean;
    config: Record<string, any>;
    limits: Record<string, number>;
    expires_at: string;
  }>): Promise<any> {
    return apiClient.patch(`/projects/project-modules/${id}/`, data);
  },

  /**
   * Delete/disable a project module
   */
  async deleteProjectModule(id: string): Promise<void> {
    return apiClient.delete(`/projects/project-modules/${id}/`);
  },

  /**
   * Bulk enable modules for a project
   */
  async bulkEnableModules(projectId: string, modules: string[]): Promise<{
    enabled: string[];
    skipped: { module: string; reason: string }[];
    enabled_count: number;
  }> {
    return apiClient.post('/projects/project-modules/bulk-enable/', {
      project: projectId,
      modules,
    });
  },

  /**
   * Toggle a module's enabled status
   */
  async toggleProjectModule(moduleId: string): Promise<{
    status: 'enabled' | 'disabled';
    module: string;
    is_enabled: boolean;
  }> {
    return apiClient.post(`/projects/project-modules/${moduleId}/toggle/`);
  },

  // =============================================================================
  // Project Permissions API
  // =============================================================================

  /**
   * Get project permissions with optional filters
   */
  async getProjectPermissions(params?: {
    project?: string;
    role?: string;
    module?: string;
    action?: string;
    has_permission?: boolean;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.project) queryParams.append('project', params.project);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.module) queryParams.append('module', params.module);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.has_permission !== undefined) queryParams.append('has_permission', String(params.has_permission));
    const url = `/projects/project-permissions/${queryParams.toString() ? `?${queryParams}` : ''}`;
    return apiClient.get(url);
  },

  /**
   * Get full permissions matrix for a project
   */
  async getProjectPermissionsMatrix(projectId: string): Promise<{
    project: string;
    roles: any[];
  }> {
    return apiClient.get(`/projects/project-permissions/matrix/?project=${projectId}`);
  },

  /**
   * Create a project permission
   */
  async createProjectPermission(data: {
    project: string;
    role: string;
    module: string;
    action: string;
    has_permission: boolean;
  }): Promise<any> {
    return apiClient.post('/projects/project-permissions/', data);
  },

  /**
   * Update a project permission
   */
  async updateProjectPermission(id: string, data: { has_permission: boolean }): Promise<any> {
    return apiClient.patch(`/projects/project-permissions/${id}/`, data);
  },

  /**
   * Bulk update project permissions
   */
  async bulkUpdateProjectPermissions(projectId: string, permissions: {
    role: string;
    module: string;
    action: string;
    has_permission: boolean;
  }[]): Promise<{
    updated: any[];
    created: any[];
    updated_count: number;
    created_count: number;
  }> {
    return apiClient.post('/projects/project-permissions/bulk-update/', {
      project: projectId,
      permissions,
    });
  },

  /**
   * Initialize project permissions from defaults
   */
  async initializeProjectPermissions(projectId: string, overwrite = false): Promise<{
    created: number;
    skipped: number;
    message: string;
  }> {
    return apiClient.post('/projects/project-permissions/copy-from-defaults/', {
      project: projectId,
      overwrite,
    });
  },

  /**
   * Restore project permissions to inherited values by deleting all project overrides.
   * This causes the cascade to fall back to company-level or global permissions.
   *
   * @param projectId - The project ID
   * @param role - The role code to restore inheritance for
   */
  async restoreProjectInheritance(projectId: string, role: string): Promise<{
    message: string;
    deleted: number;
    project: string;
    project_name: string;
    role: string;
  }> {
    return apiClient.post('/projects/project-permissions/restore-inheritance/', {
      project: projectId,
      role,
    });
  },

  // =============================================================================
  // User Project Context API
  // =============================================================================

  /**
   * Get current user's role and permissions in a specific project
   */
  async getUserProjectRole(projectId: string): Promise<{
    project: { id: string; name: string; code: string };
    membership: { id: string; joined_at: string; is_primary: boolean } | null;
    role: string | null;
    role_display: string | null;
    permissions: Record<string, Record<string, boolean>>;
    enabled_modules: string[];
    is_superuser: boolean;
  }> {
    return apiClient.get(`/projects/${projectId}/my-role/`);
  },

  // =============================================================================
  // Hierarchical Area Types API (Project -> Stage -> Block)
  // =============================================================================

  // --- Project Area Types ---

  /**
   * Get area types assigned to a project
   */
  async getProjectAreaTypes(params?: { project?: string; is_active?: boolean }): Promise<ProjectAreaTypeList[]> {
    const queryParams = new URLSearchParams();
    if (params?.project) queryParams.append('project', params.project);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    const query = queryParams.toString();
    const response = await apiClient.get<ProjectAreaTypeList[] | PaginatedResponse<ProjectAreaTypeList>>(`/projects/project-area-types/${query ? `?${query}` : ''}`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Get available area types that can be assigned to a project
   */
  async getAvailableAreaTypesForProject(projectId: string): Promise<AreaType[]> {
    const response = await apiClient.get<AreaType[] | PaginatedResponse<AreaType>>(`/projects/project-area-types/available-for-project/${projectId}/`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Assign an area type to a project
   */
  async createProjectAreaType(data: {
    project: string;
    area_type_id: string;
    default_price?: number;
    is_required?: boolean;
    is_active?: boolean;
    sort_order?: number;
    notes?: string;
  }): Promise<ProjectAreaType> {
    return apiClient.post('/projects/project-area-types/', data);
  },

  /**
   * Update a project area type
   */
  async updateProjectAreaType(id: string, data: Partial<{
    default_price: number | null;
    is_required: boolean;
    is_active: boolean;
    sort_order: number;
    notes: string;
  }>): Promise<ProjectAreaType> {
    return apiClient.patch(`/projects/project-area-types/${id}/`, data);
  },

  /**
   * Remove an area type from a project
   */
  async deleteProjectAreaType(id: string): Promise<void> {
    return apiClient.delete(`/projects/project-area-types/${id}/`);
  },

  /**
   * Check if a project area type has dependencies (stages/blocks)
   */
  async checkProjectAreaTypeDependencies(id: string): Promise<{
    has_dependencies: boolean;
    stages_count: number;
    blocks_count: number;
    stages: Array<{ id: string; name: string; stage_area_type_id: string }>;
    area_type_name: string;
  }> {
    return apiClient.get(`/projects/project-area-types/${id}/check-dependencies/`);
  },

  /**
   * Delete a project area type and all its dependencies (cascade delete)
   */
  async cascadeDeleteProjectAreaType(id: string): Promise<{
    success: boolean;
    deleted: {
      project_area_type: number;
      stage_area_types: number;
      block_area_types: number;
    };
  }> {
    return apiClient.delete(`/projects/project-area-types/${id}/cascade-delete/`);
  },

  // --- Stage Area Types ---

  /**
   * Get area types assigned to a stage
   */
  async getStageAreaTypes(params?: { stage?: string; is_active?: boolean }): Promise<StageAreaTypeList[]> {
    const queryParams = new URLSearchParams();
    if (params?.stage) queryParams.append('stage', params.stage);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    const query = queryParams.toString();
    const response = await apiClient.get<StageAreaTypeList[] | PaginatedResponse<StageAreaTypeList>>(`/projects/stage-area-types/${query ? `?${query}` : ''}`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Get available project area types that can be assigned to a stage
   */
  async getAvailableAreaTypesForStage(stageId: string): Promise<ProjectAreaTypeList[]> {
    const response = await apiClient.get<ProjectAreaTypeList[] | PaginatedResponse<ProjectAreaTypeList>>(`/projects/stage-area-types/available-for-stage/${stageId}/`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Assign an area type to a stage
   */
  async createStageAreaType(data: {
    stage: string;
    project_area_type_id: string;
    default_price?: number;
    is_required?: boolean;
    is_active?: boolean;
    sort_order?: number;
    notes?: string;
    enabled_subcategory_ids?: string[];
  }): Promise<StageAreaType> {
    return apiClient.post('/projects/stage-area-types/', data);
  },

  /**
   * Update a stage area type
   */
  async updateStageAreaType(id: string, data: Partial<{
    default_price: number | null;
    is_required: boolean;
    is_active: boolean;
    sort_order: number;
    notes: string;
    enabled_subcategory_ids: string[];
  }>): Promise<StageAreaType> {
    return apiClient.patch(`/projects/stage-area-types/${id}/`, data);
  },

  /**
   * Remove an area type from a stage
   */
  async deleteStageAreaType(id: string): Promise<void> {
    return apiClient.delete(`/projects/stage-area-types/${id}/`);
  },

  /**
   * Check if a stage area type has dependencies (blocks)
   */
  async checkStageAreaTypeDependencies(id: string): Promise<{
    has_dependencies: boolean;
    blocks_count: number;
    blocks: Array<{ id: string; name: string; block_area_type_id: string }>;
    area_type_name: string;
  }> {
    return apiClient.get(`/projects/stage-area-types/${id}/check-dependencies/`);
  },

  /**
   * Delete a stage area type and all its dependencies (cascade delete)
   */
  async cascadeDeleteStageAreaType(id: string): Promise<{
    success: boolean;
    deleted: {
      stage_area_type: number;
      block_area_types: number;
    };
  }> {
    return apiClient.delete(`/projects/stage-area-types/${id}/cascade-delete/`);
  },

  // --- Block Area Types ---

  /**
   * Get area types assigned to a block
   */
  async getBlockAreaTypes(params?: { block?: string; is_active?: boolean }): Promise<BlockAreaTypeList[]> {
    const queryParams = new URLSearchParams();
    if (params?.block) queryParams.append('block', params.block);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    const query = queryParams.toString();
    const response = await apiClient.get<BlockAreaTypeList[] | PaginatedResponse<BlockAreaTypeList>>(`/projects/block-area-types/${query ? `?${query}` : ''}`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Get available stage area types that can be assigned to a block
   */
  async getAvailableAreaTypesForBlock(blockId: string): Promise<StageAreaTypeList[]> {
    const response = await apiClient.get<StageAreaTypeList[] | PaginatedResponse<StageAreaTypeList>>(`/projects/block-area-types/available-for-block/${blockId}/`);
    // Handle paginated response
    return Array.isArray(response) ? response : (response?.results || []);
  },

  /**
   * Assign an area type to a block
   */
  async createBlockAreaType(data: {
    block: string;
    stage_area_type_id: string;
    default_price?: number;
    is_active?: boolean;
    sort_order?: number;
    notes?: string;
  }): Promise<BlockAreaType> {
    return apiClient.post('/projects/block-area-types/', data);
  },

  /**
   * Update a block area type
   */
  async updateBlockAreaType(id: string, data: Partial<{
    default_price: number | null;
    is_active: boolean;
    sort_order: number;
    notes: string;
  }>): Promise<BlockAreaType> {
    return apiClient.patch(`/projects/block-area-types/${id}/`, data);
  },

  /**
   * Remove an area type from a block
   */
  async deleteBlockAreaType(id: string): Promise<void> {
    return apiClient.delete(`/projects/block-area-types/${id}/`);
  },

  /**
   * Assign an area type to all units in the block
   * Only creates UnitAreaValue for units that don't already have this area type
   */
  async assignBlockAreaTypeToUnits(blockAreaTypeId: string, defaultValue: number = 0): Promise<{
    status: string;
    created_count: number;
    skipped_count: number;
    total_units: number;
    area_type_name: string;
  }> {
    return apiClient.post(`/projects/block-area-types/${blockAreaTypeId}/assign-to-units/`, {
      default_value: defaultValue
    });
  },

  /**
   * Remove an area type from all units in the block
   * Only removes UnitAreaValue where value is 0 (unless force=true)
   */
  async removeBlockAreaTypeFromUnits(blockAreaTypeId: string, force: boolean = false): Promise<{
    status: string;
    deleted_count: number;
    skipped_count: number;
    area_type_name: string;
  }> {
    return apiClient.delete(`/projects/block-area-types/${blockAreaTypeId}/remove-from-units/${force ? '?force=true' : ''}`);
  },

  // ==========================================================================
  // COMPLEMENTARY UNITS
  // ==========================================================================

  /**
   * Get complementary units for a block
   */
  async getComplementaryUnits(params?: {
    project?: string;
    block?: string;
    area_type?: string;
    status?: string;
  }): Promise<PaginatedResponse<ComplementaryUnitList>> {
    return apiClient.get('/projects/complementary-units/', { params });
  },

  /**
   * Get all complementary units for a specific block
   */
  async getComplementaryUnitsByBlock(blockId: string): Promise<ComplementaryUnitList[]> {
    return apiClient.get(`/projects/complementary-units/by-block/${blockId}/`);
  },

  /**
   * Get available complementary units for a block (not assigned)
   */
  async getAvailableComplementaryUnits(blockId: string, areaTypeId?: string): Promise<ComplementaryUnitList[]> {
    const params = areaTypeId ? { area_type: areaTypeId } : {};
    return apiClient.get(`/projects/complementary-units/available/${blockId}/`, { params });
  },

  /**
   * Get area types that are assignable complements for a block's project
   */
  async getComplementaryTypes(blockId: string): Promise<{
    block_area_type_id: string;
    area_type_id: string;
    area_type_code: string;
    area_type_name: string;
    area_type_icon: string;
    area_type_measurement_type: string;
    area_type_category: string;
    has_subcategories: boolean;
    subcategories: Array<{
      id: string;
      code: string;
      name: string;
      icon: string;
      quantity: number;
      price: number | null;
    }>;
    base_price: number | null;
  }[]> {
    return apiClient.get(`/projects/complementary-units/types/${blockId}/`);
  },

  /**
   * Get statistics for complementary units in a block
   */
  async getComplementaryUnitStats(blockId: string): Promise<ComplementaryUnitStats> {
    return apiClient.get(`/projects/complementary-units/stats/${blockId}/`);
  },

  async getComplementaryUnitStatsByProject(projectId: string): Promise<ComplementaryUnitStats & { total_estimated_value: number; available_value: number; assigned_value: number; reserved_value: number; sold_value: number }> {
    return apiClient.get(`/projects/complementary-units/stats-by-project/${projectId}/`);
  },

  /**
   * Sync prices for complementary units from pricing scheme
   */
  async syncComplementaryUnitPrices(blockId: string): Promise<{ message: string; updated_count: number; total_units: number }> {
    return apiClient.post(`/projects/complementary-units/sync-prices/${blockId}/`);
  },

  /**
   * Get a single complementary unit
   */
  async getComplementaryUnit(id: string): Promise<ComplementaryUnit> {
    return apiClient.get(`/projects/complementary-units/${id}/`);
  },

  /**
   * Create a new complementary unit
   */
  async createComplementaryUnit(data: {
    block: string;
    area_type_id: string;
    subcategory_id?: string;
    identifier: string;
    area?: number;
    location?: string;
    estimated_price: number;
    notes?: string;
  }): Promise<ComplementaryUnit> {
    return apiClient.post('/projects/complementary-units/', data);
  },

  /**
   * Update a complementary unit
   */
  async updateComplementaryUnit(id: string, data: Partial<{
    identifier: string;
    area: number;
    location: string;
    estimated_price: number;
    notes: string;
    status: string;
  }>): Promise<ComplementaryUnit> {
    return apiClient.patch(`/projects/complementary-units/${id}/`, data);
  },

  /**
   * Delete a complementary unit
   */
  async deleteComplementaryUnit(id: string): Promise<void> {
    return apiClient.delete(`/projects/complementary-units/${id}/`);
  },

  // ==========================================================================
  // UNIT COMPLEMENTARY ASSIGNMENTS
  // ==========================================================================

  /**
   * Get complementary assignments for a unit
   */
  async getUnitComplementaryAssignments(unitId: string): Promise<UnitComplementaryAssignment[]> {
    return apiClient.get(`/projects/unit-complementary-assignments/by-unit/${unitId}/`);
  },

  /**
   * Assign a complementary unit to a unit
   */
  async assignComplementaryUnit(data: {
    unit_id: string;
    complementary_unit_id: string;
    sale_price?: number;
    notes?: string;
  }): Promise<UnitComplementaryAssignment> {
    return apiClient.post('/projects/unit-complementary-assignments/assign/', data);
  },

  /**
   * Remove an assignment (unassign complementary unit from unit)
   */
  async unassignComplementaryUnit(assignmentId: string): Promise<void> {
    return apiClient.post(`/projects/unit-complementary-assignments/${assignmentId}/unassign/`);
  },

  /**
   * Delete an assignment directly
   */
  async deleteComplementaryAssignment(assignmentId: string): Promise<void> {
    return apiClient.delete(`/projects/unit-complementary-assignments/${assignmentId}/`);
  },

  // ============================================
  // GLB STAGES AUTO-CREATION
  // ============================================

  /**
   * Create stages from GLB analysis
   * Used when stages_mask GLB detects stages that don't exist in DB
   * @param projectId - UUID of the Project
   * @param stages - Array of stage data to create
   */
  async createStagesFromGLB(
    projectId: string,
    stages: Array<{ name: string; code: string }>
  ): Promise<{
    success: boolean;
    created: number;
    stages: Array<{ id: string; name: string; code: string }>;
  }> {
    return apiClient.post(`/projects/${projectId}/create-stages-from-glb/`, { stages });
  },

  /**
   * Create stages and blocks from GLB entity detection
   * Used when main GLB detects stages/blocks that don't exist in DB (single-stage mode)
   * @param projectId - UUID of the Project
   * @param stages - Array of stage data to create
   * @param blocks - Array of block data to create
   */
  async createEntitiesFromGLB(
    projectId: string,
    stages: Array<{ name: string; code: string }>,
    blocks: Array<{ name: string; code: string; stage_code: string }>
  ): Promise<{
    success: boolean;
    created_stages: number;
    existing_stages: number;
    created_blocks: number;
    existing_blocks: number;
    total_stages: number;
    total_blocks: number;
    stages: Array<{ id: string; name: string; code: string; status: string }>;
    blocks: Array<{ id: string; name: string; code: string; stage_id: string; stage_name: string; status: string }>;
  }> {
    return apiClient.post(`/projects/${projectId}/create-entities-from-glb/`, { stages, blocks });
  },

  /**
   * Get available stages for an OrbitView
   * Used to refresh stages after creating them
   * @param orbitViewId - UUID of the OrbitView
   */
  async getOrbitViewStages(orbitViewId: string): Promise<{
    available_stages: Array<{
      id: string;
      name: string;
      code: string;
      stage_number: string;
      has_apartments_model: boolean;
      model_path: string | null;
    }>;
  }> {
    return apiClient.get(`/projects/orbit-views/${orbitViewId}/stages/`);
  },
};

